import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useIsMobile } from './lib/responsive';
import { Routes, Route, Link, useNavigate, Navigate } from 'react-router-dom';
import { modulosVisibles, RUTAS_ANTERIORES } from './v2/hotelModulos';
import {
  Calendar as CalendarIcon, Plus, User, Pencil, Trash2,
  Ship, Home, MapPin, Clock, LogOut, Users, Briefcase, Settings as SettingsIcon,
  Menu, X, Wallet, BarChart3, PieChart, List, UserCheck, ChevronDown, ChevronRight, Receipt,
  LayoutDashboard, FileText, Wrench, Brush, MessageCircle,
  Calendar, TrendingUp, TrendingDown, Key, Activity, QrCode, BookOpen, ClipboardCheck, Zap, Bell,
  Award,
} from 'lucide-react';
import Swal from 'sweetalert2'; 

// Importación de componentes existentes
import PropertyDetails from './PropertyDetails';
import AdminHeader from './AdminHeader'; 
import Hero from './Hero'; 

// Finanzas y Proveedores

// Nómina

// Camareras

// Mantenimiento

// Home público
import HomeView from './HomeView';

// Configuración del Sistema
import PropiedadPublica   from './PropiedadPublica';
import ExperienciaPublica from './ExperienciaPublica';
import LegalDocsModal from './LegalDocsModal';
import { cargarHoteles } from './v2/HomeHoteles';

// ── Carga diferida: cada pantalla se descarga cuando se abre, no al entrar al sitio ──
const AgregarPropiedades = lazy(() => import('./AgregarPropiedades'));
const AdminExperiencias = lazy(() => import('./AdminExperiencias'));
const MultiPropertyCalendar = lazy(() => import('./MultiPropertyCalendar'));
const ReservationsManager = lazy(() => import('./ReservationsManager'));
const ExperienceReservationsManager = lazy(() => import('./ExperienceReservationsManager'));
const AgregarPropietario = lazy(() => import('./AgregarPropietario'));
const ListaPropietarios = lazy(() => import('./ListaPropietarios'));
const ClientePerfil = lazy(() => import('./ClientePerfil'));
const Registro = lazy(() => import('./Registro'));
const VerificacionIdentidad = lazy(() => import('./VerificacionIdentidad'));
const ListaPropiedades = lazy(() => import('./ListaPropiedades'));
const ChecklistsAptos = lazy(() => import('./v2/ChecklistsAptos'));
const ReservaAlojamiento = lazy(() => import('./ReservaAlojamiento'));
const CheckoutReserva = lazy(() => import('./CheckoutReserva'));
const ContabilidadDashboard = lazy(() => import('./contabilidad'));
const Proveedores = lazy(() => import('./Proveedores'));
const ListaProveedores = lazy(() => import('./ListaProveedores'));
const AdminProveedores = lazy(() => import('./AdminProveedores'));
const ListaExperiencias = lazy(() => import('./ListaExperiencias'));
const ListaPagosProveedores = lazy(() => import('./ListaPagosProveedores'));
const RegistroPagoProveedores = lazy(() => import('./RegistroPagoProveedores'));
const PagosExperienciasProveedores = lazy(() => import('./PagosExperienciasProveedores'));
const GestionHumana = lazy(() => import('./gestionhumana'));
const ListaTrabajadores = lazy(() => import('./listatrabajadores'));
const PagoNomina = lazy(() => import('./pagonomina'));
const ListaPagoNomina = lazy(() => import('./ListaPagoNomina').then(mod => ({ default: mod.ListaPagoNomina })));
const PanelCamareras = lazy(() => import('./PanelCamareras'));
const MaintenanceDashboard = lazy(() => import('./MaintenanceDashboard'));
const RegisterMaintenance = lazy(() => import('./RegisterMaintenance'));
const DashboardGeneral = lazy(() => import('./DashboardGeneral'));
const ConfiguracionSistema = lazy(() => import('./ConfiguracionSistema'));
const SuperAdminPanel = lazy(() => import('./SuperAdminPanel'));
const CRM = lazy(() => import('./CRM'));
const LeadPipeline = lazy(() => import('./LeadPipeline'));
const AutomationEngine = lazy(() => import('./AutomationEngine'));
const VyaraDashboard = lazy(() => import('./VyaraDashboard'));
const Julieta = lazy(() => import('./Julieta'));
const EstadoCuentaPropietarios = lazy(() => import('./EstadoCuentaPropietarios'));
const AnalisisPropietario = lazy(() => import('./AnalisisPropietario'));
const EgresosDashboard = lazy(() => import('./EgresosDashboard'));
const EstudioMercado = lazy(() => import('./EstudioMercado'));
const PropietarioPerfil = lazy(() => import('./PropietarioPerfil'));
const RegistroPropietario = lazy(() => import('./RegistroPropietario'));
const AdminMessages = lazy(() => import('./AdminMessages'));
const AdminDocumentos = lazy(() => import('./AdminDocumentos'));
const PaginaMensajes = lazy(() => import('./PaginaMensajes'));
const MisReservas = lazy(() => import('./MisReservas'));
const PropietarioEstadoCuenta = lazy(() => import('./PropietarioEstadoCuenta'));
const PropietarioCalendario = lazy(() => import('./PropietarioCalendario'));
const OtrosIngresos = lazy(() => import('./OtrosIngresos'));
const OtrosGastos = lazy(() => import('./OtrosGastos'));
const DistribucionUtilidades = lazy(() => import('./DistribucionUtilidades'));
const ReciboCajaList = lazy(() => import('./ReciboCajaList'));
const SolicitudesPendientes = lazy(() => import('./SolicitudesPendientes'));
const AdminRoles = lazy(() => import('./AdminRoles'));
const PagoConfirmado = lazy(() => import('./PagoConfirmado'));
const PagarReserva = lazy(() => import('./PagarReserva'));
const GuiaHuesped = lazy(() => import('./GuiaHuesped'));
const GestionGuias = lazy(() => import('./GestionGuias'));
const RevenueManager = lazy(() => import('./RevenueManager'));
const AnalyticsDashboard = lazy(() => import('./AnalyticsDashboard'));
const KPIsProfesionales = lazy(() => import('./KPIsProfesionales'));
const AnalyticsHub = lazy(() => import('./AnalyticsHub'));
const MantenimientoPreventivo = lazy(() => import('./MantenimientoPreventivo'));
const OcrRecibos = lazy(() => import('./OcrRecibos'));
const Facturacion = lazy(() => import('./Facturacion'));
const CheckInDigital = lazy(() => import('./CheckInDigital'));
const GestionCheckIns = lazy(() => import('./GestionCheckIns'));
const ConfirmarOferta = lazy(() => import('./ConfirmarOferta'));
const LoginCamarera = lazy(() => import('./LoginCamarera'));
const VerificarEmail = lazy(() => import('./VerificarEmail'));
const RegistroCamarera = lazy(() => import('./RegistroCamarera'));
const PortalCamarera = lazy(() => import('./PortalCamarera'));
const PendingScreen = lazy(() => import('./PortalCamarera').then(mod => ({ default: mod.PendingScreen })));
const LoginTrabajador = lazy(() => import('./LoginTrabajador'));
const RegistroTrabajador = lazy(() => import('./RegistroTrabajador'));
const PortalTrabajador = lazy(() => import('./PortalTrabajador'));
const PendienteTrabajador = lazy(() => import('./PortalTrabajador').then(mod => ({ default: mod.PendienteTrabajador })));
const TareasHoy = lazy(() => import('./TareasHoy'));
const ManualUso = lazy(() => import('./ManualUso'));
const Aviso2FA = lazy(() => import('./v2/Aviso2FA'));
const PortalPropietarios = lazy(() => import('./PortalPropietarios'));
const RegistroVendedor = lazy(() => import('./RegistroVendedor'));
const PortalVendedor = lazy(() => import('./PortalVendedor'));
const AdminVendedores = lazy(() => import('./AdminVendedores'));
const CotizacionPage = lazy(() => import('./CotizacionPage'));
const RegistroProveedor = lazy(() => import('./RegistroProveedor'));
const PortalProveedor = lazy(() => import('./PortalProveedor'));
const ProveedorCompletarRegistro = lazy(() => import('./ProveedorCompletarRegistro'));
const AdminJulieta = lazy(() => import('./AdminJulieta'));
const JulietaPublica = lazy(() => import('./JulietaPublica'));
const PropietarioNotificaciones = lazy(() => import('./PropietarioNotificaciones'));

// ── Vyara 2.0: pantallas nuevas con carga diferida (no pesan en la carga inicial) ──
const HotelesAdmin        = lazy(() => import('./v2/HotelesAdmin'));
const HotelesTablero      = lazy(() => import('./v2/HotelesTablero'));
const ResumenHotel        = lazy(() => import('./v2/HotelesTablero').then(m => ({ default: m.ResumenHotel })));
const HotelEspacio        = lazy(() => import('./v2/HotelEspacio'));
const ConAlcanceHotel     = lazy(() => import('./v2/HotelEspacio').then(m => ({ default: m.ConAlcanceHotel })));
const IrAlHotel           = lazy(() => import('./v2/HotelEspacio').then(m => ({ default: m.IrAlHotel })));
const MenuHoteles         = lazy(() => import('./v2/MenuHoteles'));
const GuiasAdmin          = lazy(() => import('./v2/GuiasAdmin'));
const PopupsAdmin         = lazy(() => import('./v2/PopupsAdmin'));
const ConciliacionExperiencias = lazy(() => import('./v2/ConciliacionExperiencias'));
const Cotizaciones        = lazy(() => import('./v2/Cotizaciones'));
const Fidelizacion        = lazy(() => import('./v2/Fidelizacion'));

// Enlace de recomendación (?ref=CODIGO): se guarda para proponerlo en «Mis puntos» cuando la persona tenga su cuenta
try { const ref = new URLSearchParams(window.location.search).get('ref'); if (ref && /^[A-Za-z0-9]{4,20}$/.test(ref)) localStorage.setItem('refVyara', ref.toUpperCase()); } catch {}
const CotizacionCliente   = lazy(() => import('./v2/CotizacionCliente'));
const HotelTablero        = lazy(() => import('./v2/HotelTablero'));
const HotelRecepcion      = lazy(() => import('./v2/HotelRecepcion'));
const HotelPago           = lazy(() => import('./v2/HotelPago'));
const HotelAseo           = lazy(() => import('./v2/HotelAseo'));
const PortalPersonal      = lazy(() => import('./v2/PortalPersonal'));
const IngresoHoteles      = lazy(() => import('./v2/IngresoHoteles'));
const HotelRestaurante    = lazy(() => import('./v2/HotelRestaurante'));
const HotelComidas        = lazy(() => import('./v2/HotelComidas'));
const HotelMantenimiento  = lazy(() => import('./v2/HotelMantenimiento'));
const HotelProveedores    = lazy(() => import('./v2/HotelProveedores'));
const HotelCompras        = lazy(() => import('./v2/HotelCompras'));
const HotelEspacios       = lazy(() => import('./v2/HotelEspacios'));
const HotelComercial      = lazy(() => import('./v2/HotelComercial'));
const HotelCierres        = lazy(() => import('./v2/HotelCierres'));
const HotelUsuarios       = lazy(() => import('./v2/HotelUsuarios'));
const HotelOpinion        = lazy(() => import('./v2/HotelOpinion'));
const HotelReservar       = lazy(() => import('./v2/HotelReservar'));
const ContabilidadAlcance = lazy(() => import('./v2/ContabilidadAlcance'));
const NominaAlcance       = lazy(() => import('./v2/NominaAlcance'));
const HotelPublico        = lazy(() => import('./v2/HotelPublico'));
const HotelCheckIn        = lazy(() => import('./v2/HotelCheckIn'));
const HotelesPagina       = lazy(() => import('./v2/HomeHoteles'));
const MisReservasHotel    = lazy(() => import('./v2/MisReservasHotel'));
const cargandoPantalla = <div style={{ padding: 60, textAlign: 'center', color: '#64748b', fontFamily: 'Inter, system-ui, sans-serif' }}>Cargando…</div>;
const V2 = ({ children }) => <Suspense fallback={cargandoPantalla}>{children}</Suspense>;

// --- COMPONENTE AUXILIAR PARA GRUPOS COLAPSABLES ---
const SidebarGroup = ({ title, icon: Icon, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div style={{ marginBottom: '5px' }}>
      <div 
        onClick={() => setIsOpen(!isOpen)} 
        style={{ ...sidebarSectionTitle, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: isOpen ? '#f8fafc' : 'transparent', borderRadius: '10px', padding: '10px 15px' }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {Icon && <Icon size={14} />} {title}
        </span>
        {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </div>
      {isOpen && <div style={{ paddingLeft: '10px', display: 'flex', flexDirection: 'column', gap: '2px' }}>{children}</div>}
    </div>
  );
};

// Roles cuyo acceso se rige módulo por módulo según "Roles y Accesos" (igual que MODULE_ROLES en src/lib/serverAuth.js)
const ROLES_POR_MODULO = ['SUPERVISORA', 'GERENTE_HOTEL', 'RECEPCION', 'AMA_LLAVES', 'COCINA', 'MANT_HOTEL', 'CONTADOR_HOTEL', 'ADMIN_HOTEL', 'ROL_HOTEL'];
// Quién administra los usuarios y roles de un hotel: Vyara, o su gerente o administrador (igual que puedeAdministrar en src/lib/hotelUsuarios.js)
// Permisos cuando el usuario no tiene permisos guardados (mismos de la plantilla en AdminRoles.js)
const PERMISOS_POR_DEFECTO = {
  SUPERVISORA: { calendario: true, propiedades: true, camareras: true },
  GERENTE_HOTEL: { cotizaciones: true, hoteles: true, hotel_tablero: true, hotel_recepcion: true, hotel_aseo: true, hotel_restaurante: true, hotel_mantenimiento: true, hotel_proveedores: true, hotel_compras: true, hotel_dotacion: true, hotel_comercial: true, hotel_informes: true, contabilidad_alcance: true, nomina_alcance: true, otros_gastos: true, otros_ingresos: true },
  ADMIN_HOTEL: { cotizaciones: true, hoteles: true, hotel_tablero: true, hotel_recepcion: true, hotel_aseo: true, hotel_restaurante: true, hotel_mantenimiento: true, hotel_proveedores: true, hotel_compras: true, hotel_dotacion: true, hotel_comercial: true, hotel_informes: true, contabilidad_alcance: true, nomina_alcance: true, otros_gastos: true, otros_ingresos: true },
  RECEPCION: { cotizaciones: true, hotel_tablero: true, hotel_recepcion: true, hotel_aseo: true, hotel_restaurante: true, hotel_mantenimiento: true },
  AMA_LLAVES: { hotel_tablero: true, hotel_aseo: true },
  COCINA: { hotel_restaurante: true },
  MANT_HOTEL: { hotel_mantenimiento: true, hotel_aseo: true },
  CONTADOR_HOTEL: { hotel_proveedores: true, hotel_compras: true, hotel_informes: true, contabilidad_alcance: true, nomina_alcance: true, otros_gastos: true, otros_ingresos: true },
};
// Primera pantalla de un rol de hotel, según lo que tenga activo
const RUTAS_HOTEL = [['hotel_recepcion', '/admin/hoteles/recepcion'], ['hotel_tablero', '/admin/hoteles/tablero'], ['hotel_aseo', '/admin/hoteles/aseo'], ['hotel_restaurante', '/admin/hoteles/restaurante'], ['hotel_mantenimiento', '/admin/hoteles/mantenimiento'], ['hotel_proveedores', '/admin/hoteles/proveedores'], ['hotel_compras', '/admin/hoteles/compras'], ['hotel_dotacion', '/admin/hoteles/espacios'], ['hotel_comercial', '/admin/hoteles/comercial'], ['hotel_informes', '/admin/hoteles/cierres'], ['contabilidad_alcance', '/admin/contabilidad-negocio'], ['nomina_alcance', '/admin/nomina-negocio'], ['hoteles', '/admin/hoteles']];

// Sección plegable del menú (General, Apartamentos, Hoteles, Empresa Vyara, Configuración): solo una abierta a la vez
const SidebarSection = ({ id, title, tag, icon: Icon, abierta, alAbrir, children }) => {
  const open = abierta === id;
  return (
    <div style={{ marginBottom: 6 }}>
      <button type="button" onClick={() => alAbrir(open ? null : id)} aria-expanded={open}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '13px 14px', borderRadius: 12, cursor: 'pointer', fontFamily: 'inherit',
          border: `1px solid ${open ? '#003366' : '#e2e8f0'}`, background: open ? '#003366' : '#fff', color: open ? '#fff' : '#003366' }}>
        {Icon && <Icon size={17} />}
        <span style={{ fontSize: 13, fontWeight: 900, letterSpacing: 0.6, textTransform: 'uppercase', flex: 1, textAlign: 'left' }}>{title}</span>
        {tag && <span style={{ fontSize: 9, fontWeight: 900, background: open ? '#fff' : '#003366', color: open ? '#003366' : '#fff', borderRadius: 999, padding: '1px 7px', letterSpacing: 0.4 }}>{tag}</span>}
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>
      {open && <div style={{ display: 'flex', flexDirection: 'column', gap: 5, padding: '8px 0 6px 6px' }}>{children}</div>}
    </div>
  );
};

// --- COMPONENTE SIDEBAR ---
const Sidebar = ({ isOpen, toggle, auth, onLogout, openProveedorModal, adminUnreadMessages = 0, hasAccess }) => {
  const [seccionAbierta, setSeccionAbierta] = useState(() => { try { return localStorage.getItem('menuSeccion') || 'general'; } catch { return 'general'; } });
  const abrirSeccion = (id) => { setSeccionAbierta(id); try { localStorage.setItem('menuSeccion', id || ''); } catch {} };
  const acordeon = { abierta: seccionAbierta, alAbrir: abrirSeccion };
  if (!isOpen) return null;
  const userRole = auth.role ? auth.role.toUpperCase() : '';
  const isAdmin = userRole === 'ADMIN' || userRole === 'SUPER_ADMIN';
  // Roles por módulo: cada enlace depende de su permiso; el resto conserva su regla de siempre
  const porModulo = ROLES_POR_MODULO.includes(userRole);
  const ve = (key, legacy = isAdmin) => (porModulo ? hasAccess(key) : legacy);
  const veAlguno = (keys, legacy = isAdmin) => (porModulo ? keys.some(k => hasAccess(k)) : legacy);
  const legacyProps = isAdmin || userRole === 'EDITOR_PROPS' || hasAccess('props');
  const legacyExps = isAdmin || userRole === 'EDITOR_EXPS' || hasAccess('exps');
  // Secciones del menú (Vyara 2.0): cada título aparece solo si la persona ve algo dentro
  let veCotizaciones = isAdmin || userRole === 'VENDEDOR_INTERNO';
  try { veCotizaciones = veCotizaciones || !!JSON.parse(auth.permissions || 'null')?.cotizaciones; } catch {}
  const veGeneral = veCotizaciones || isAdmin || hasAccess('dashboard') || veAlguno(['mercado', 'mensajes', 'analytics', 'crm', 'automatizaciones', 'vendedores', 'guias', 'popups', 'cotizaciones', 'fidelizacion']) || userRole === 'VENDEDOR_INTERNO' || veAlguno(['calendario', 'reservas'], legacyProps) || veAlguno(['experiencias', 'reservas_exp'], legacyExps);
  const veApartamentos = ve('propiedades', legacyProps) || veAlguno(['propietarios', 'estados_cuenta', 'camareras', 'mantenimiento', 'nomina', 'pagos_nomina']);
  const veHoteles = veAlguno(['hoteles', 'hotel_tablero', 'hotel_recepcion', 'hotel_aseo', 'hotel_restaurante', 'hotel_mantenimiento', 'hotel_proveedores', 'hotel_compras', 'hotel_dotacion', 'hotel_comercial', 'hotel_informes']);
  const veEmpresa = veAlguno(['proveedores', 'pagos_prov', 'contabilidad', 'otros_ingresos', 'otros_gastos', 'analytics', 'mantenimiento', 'contabilidad_alcance', 'nomina_alcance']);
  let sinRestriccion = true;
  try { const p = auth.permissions ? JSON.parse(auth.permissions) : null; sinRestriccion = !(Array.isArray(p?._alcance) && p._alcance.length) || userRole === 'SUPER_ADMIN'; } catch {}

  return (
    <>
      <div style={{...overlayStyle, zIndex: 7999}} onClick={toggle} />
      <div style={{...sidebarStyle, zIndex: 8000}}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
          <div style={{ fontWeight: '900', color: '#003366', fontSize: '18px' }}>MENÚ SISTEMA</div>
          <X onClick={toggle} style={{ cursor: 'pointer', color: '#666' }} />
        </div>

        <div style={{...sidebarLinksContainer, overflowY: 'auto'}}>
          <Link to="/" onClick={toggle} style={sidebarLink}><Home size={18}/> Vista Pública</Link>

          {/* ═══ GENERAL ═══ */}
          {veGeneral && <SidebarSection id="general" title="General" icon={LayoutDashboard} {...acordeon}>
          {(isAdmin || hasAccess('dashboard')) && (
            <Link to="/admin/dashboard" onClick={toggle} style={{...sidebarLink, backgroundColor: '#f0f9ff', color: '#003366'}}>
              <LayoutDashboard size={18}/> Dashboard Realtime
            </Link>
          )}

          {ve('mercado') && (
            <Link to="/admin/estudio-mercado" onClick={toggle} style={{...sidebarLink, backgroundColor: '#fffbeb', color: '#b45309', fontWeight: '900', border: '1px solid #fde68a', borderRadius: '10px', marginBottom: '4px'}}>
              ✨ Estudio de Mercado
            </Link>
          )}

          {ve('mensajes') && (
            <Link to="/admin/mensajes" onClick={toggle} style={{...sidebarLink, backgroundColor: '#fff1f2', color: '#be123c', fontWeight: '900', border: '1px solid #fecdd3', borderRadius: '10px', marginBottom: '4px', justifyContent: 'space-between'}}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Users size={18}/> Mensajes
              </span>
              {adminUnreadMessages > 0 && <span style={menuBadge}>{adminUnreadMessages}</span>}
            </Link>
          )}

          {ve('analytics') && (
            <Link to="/admin/analytics-hub" onClick={toggle} style={{...sidebarLink, backgroundColor: '#f0f9ff', color: '#0369a1', fontWeight: '900', border: '1px solid #bae6fd', borderRadius: '10px', marginBottom: '4px'}}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <BarChart3 size={18} color="#0369a1"/> Analytics · BI
              </span>
            </Link>
          )}
          {ve('crm') && (
            <Link to="/admin/crm" onClick={toggle} style={{...sidebarLink, backgroundColor: '#faf5ff', color: '#7c3aed', fontWeight: '900', border: '1px solid #e9d5ff', borderRadius: '10px', marginBottom: '4px'}}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Users size={18} color="#7c3aed"/> CRM · Clientes
              </span>
            </Link>
          )}
          {ve('crm') && (
            <Link to="/admin/leads" onClick={toggle} style={{...sidebarLink, backgroundColor: '#fff7ed', color: '#c2410c', fontWeight: '900', border: '1px solid #fed7aa', borderRadius: '10px', marginBottom: '4px'}}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <TrendingUp size={18} color="#c2410c"/> Pipeline de Ventas
              </span>
            </Link>
          )}
          {ve('guias') && (
            <Link to="/admin/guias" onClick={toggle} style={{...sidebarLink, backgroundColor: '#fdf2f8', color: '#be185d', fontWeight: '900', border: '1px solid #fbcfe8', borderRadius: '10px', marginBottom: '4px'}}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <BookOpen size={18} color="#be185d"/> Guías de destino
              </span>
            </Link>
          )}
          {ve('popups') && (
            <Link to="/admin/popups" onClick={toggle} style={{...sidebarLink, backgroundColor: '#ecfdf5', color: '#047857', fontWeight: '900', border: '1px solid #a7f3d0', borderRadius: '10px', marginBottom: '4px'}}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Bell size={18} color="#047857"/> Mensajes emergentes
              </span>
            </Link>
          )}
          {ve('automatizaciones') && (
            <Link to="/admin/automatizaciones" onClick={toggle} style={{...sidebarLink, backgroundColor: '#f5f3ff', color: '#6d28d9', fontWeight: '900', border: '1px solid #ddd6fe', borderRadius: '10px', marginBottom: '4px'}}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Zap size={18} color="#6d28d9"/> Automatizaciones
              </span>
            </Link>
          )}
          {ve('cotizaciones', veCotizaciones) && (
            <Link to="/admin/cotizaciones" onClick={toggle} style={{...sidebarLink, backgroundColor: '#eff6ff', color: '#003366', fontWeight: '900', border: '1px solid #bfdbfe', borderRadius: '10px', marginBottom: '4px'}}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FileText size={18} color="#003366"/> Cotizaciones
              </span>
            </Link>
          )}
          {ve('fidelizacion') && (
            <Link to="/admin/fidelizacion" onClick={toggle} style={{...sidebarLink, backgroundColor: '#fefce8', color: '#a16207', fontWeight: '900', border: '1px solid #fde68a', borderRadius: '10px', marginBottom: '4px'}}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Award size={18} color="#a16207"/> Programa de puntos
              </span>
            </Link>
          )}
          {ve('vendedores') && (
            <Link to="/admin/vendedores" onClick={toggle} style={{...sidebarLink, backgroundColor: '#fff7ed', color: '#b45309', fontWeight: '900', border: '1px solid #fed7aa', borderRadius: '10px', marginBottom: '4px'}}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                🤝 Vendedores Aliados
              </span>
            </Link>
          )}

          {veAlguno(['calendario', 'reservas'], legacyProps) && (
            <SidebarGroup title="RESERVAS" icon={CalendarIcon}>
              {ve('calendario', true) && <Link to="/admin/calendario" onClick={toggle} style={sidebarLink}><CalendarIcon size={16}/> Calendario Maestro</Link>}
              {ve('reservas', true) && <Link to="/admin/nueva-reserva" onClick={toggle} style={sidebarLink}><Plus size={16} color="#2ecc71"/> Nueva Reserva</Link>}
              {ve('reservas', true) && <Link to="/admin/reservas"   onClick={toggle} style={sidebarLink}><Briefcase size={16}/> Lista de Reservas</Link>}
              {ve('reservas', true) && <Link to="/admin/solicitudes-pendientes" onClick={toggle} style={{...sidebarLink, color: '#7c3aed', fontWeight: 800}}><Clock size={16} color="#7c3aed"/> Solicitudes Pendientes</Link>}
              {ve('reservas', true) && <Link to="/admin/check-ins" onClick={toggle} style={{...sidebarLink, color: '#0369a1'}}><ClipboardCheck size={16} color="#0369a1"/> Check-in Digital</Link>}
            </SidebarGroup>
          )}

          {veAlguno(['experiencias', 'reservas_exp', 'conciliacion_exp'], legacyExps) && (
            <SidebarGroup title="EXPERIENCIAS" icon={Ship}>
              {ve('reservas_exp', true) && <Link to="/admin/reservas-experiencias" onClick={toggle} style={sidebarLink}><Ship size={16}/> Reservas</Link>}
              {ve('experiencias', true) && <Link to="/admin/experiencia" onClick={toggle} style={sidebarLink}><Plus size={16}/> Crear Experiencia</Link>}
              {ve('experiencias', true) && <Link to="/admin/experiencias-lista" onClick={toggle} style={sidebarLink}><List size={16}/> Ver Todas</Link>}
              {ve('conciliacion_exp') && <Link to="/admin/conciliacion-experiencias" onClick={toggle} style={{...sidebarLink, color: '#059669', fontWeight: 800}}><PieChart size={16}/> Conciliación y utilidad</Link>}
            </SidebarGroup>
          )}
          </SidebarSection>}

          {/* ═══ APARTAMENTOS ═══ */}
          {veApartamentos && <SidebarSection id="apartamentos" title="Apartamentos" icon={Home} {...acordeon}>
          {ve('propiedades', legacyProps) && (
            <SidebarGroup title="PROPIEDADES" icon={Home}>
              <Link to="/admin/AgregarPropiedades" onClick={toggle} style={sidebarLink}><Plus size={16}/> Crear Propiedad</Link>
              <Link to="/admin/propiedades-lista" onClick={toggle} style={sidebarLink}><List size={16}/> Lista de Propiedades</Link>
              <Link to="/admin/listas-chequeo" onClick={toggle} style={sidebarLink}><ClipboardCheck size={16}/> Listas de chequeo</Link>
              <Link to="/admin/guias-huesped" onClick={toggle} style={{...sidebarLink, color: '#0369a1', fontWeight: 800}}><QrCode size={16} color="#0369a1"/> Guía del Huésped</Link>
            </SidebarGroup>
          )}

          {veAlguno(['propietarios', 'estados_cuenta']) && (
            <SidebarGroup title="PROPIETARIOS" icon={Users}>
              {ve('propietarios', true) && <Link to="/admin/registrar-propietario" onClick={toggle} style={sidebarLink}>
                <Plus size={16} color="#6366f1"/> Registrar Propietario
              </Link>}
              {ve('propietarios', true) && <Link to="/admin/propietarios" onClick={toggle} style={sidebarLink}>
                <List size={16}/> Lista de Propietarios
              </Link>}
              {ve('estados_cuenta', true) && <Link to="/admin/estados-cuenta" onClick={toggle} style={sidebarLink}>
                <FileText size={16} color="#f59e0b"/> Estados de Cuenta
              </Link>}
            </SidebarGroup>
          )}
          
          {(isAdmin || porModulo) && (
            <>
              {ve('camareras') && <SidebarGroup title="CAMARERAS" icon={Brush}>
                <Link to="/admin/camareras" onClick={toggle} style={sidebarLink}>
                  <LayoutDashboard size={16}/> Panel Camareras
                </Link>
              </SidebarGroup>}

              {ve('mantenimiento') && <SidebarGroup title="MANTENIMIENTO" icon={Wrench}>
                <Link to="/admin/mantenimiento" onClick={toggle} style={sidebarLink}>
                  <LayoutDashboard size={16}/> Panel Mantenimiento
                </Link>
                <Link to="/admin/mantenimiento/nuevo" onClick={toggle} style={sidebarLink}>
                  <Plus size={16} color="#6366f1"/> Crear Reporte
                </Link>
              </SidebarGroup>}

              {veAlguno(['nomina', 'pagos_nomina']) && <SidebarGroup title="NÓMINA Y RRHH" icon={UserCheck}>
                {ve('nomina', true) && <Link to="/admin/gestion-humana" onClick={toggle} style={sidebarLink}>
                  <Plus size={16} color="#6366f1"/> Registrar Personal
                </Link>}
                {ve('nomina', true) && <Link to="/admin/trabajadores-lista" onClick={toggle} style={sidebarLink}>
                  <UserCheck size={16}/> Lista Trabajadores
                </Link>}
                <a href="/trabajador/login" target="_blank" rel="noreferrer" onClick={toggle} style={{...sidebarLink, color: '#7c3aed', fontWeight: 700}}>
                  🔗 Portal de Aliados
                </a>
                {ve('nomina', true) && <Link to="/admin/pago-nomina" onClick={toggle} style={sidebarLink}>
                  <Wallet size={16} color="#2ecc71"/> Generar Pago
                </Link>}
                {ve('pagos_nomina', true) && <Link to="/admin/lista-pagos-nomina" onClick={toggle} style={sidebarLink}>
                  <List size={16} color="#003366"/> Historial de Pagos
                </Link>}
              </SidebarGroup>}

              {ve('contabilidad_alcance') && <Link to="/admin/contabilidad-negocio?alcance=APTOS" onClick={toggle} style={sidebarLink}><PieChart size={16}/> Contabilidad de apartamentos</Link>}
              {ve('nomina_alcance') && <Link to="/admin/nomina-negocio?alcance=APTOS" onClick={toggle} style={sidebarLink}><UserCheck size={16}/> Nómina de apartamentos</Link>}
            </>
          )}
          </SidebarSection>}

          {/* ═══ HOTELES ═══ */}
          {veHoteles && (
            <SidebarSection id="hoteles" title="Hoteles" tag="NUEVO" icon={Briefcase} {...acordeon}>
              <Suspense fallback={null}><MenuHoteles estilo={sidebarLink} alTocar={toggle} /></Suspense>
              {ve('hoteles') && <Link to="/admin/hoteles" onClick={toggle} style={sidebarLink}><Home size={16}/> Registrar y editar hoteles</Link>}
              {ve('nomina_alcance') && <Link to="/admin/nomina-negocio?alcance=HOTELES" onClick={toggle} style={sidebarLink}><UserCheck size={16}/> Nómina de todos los hoteles</Link>}
              {ve('contabilidad_alcance') && <Link to="/admin/contabilidad-negocio?alcance=HOTELES" onClick={toggle} style={sidebarLink}><PieChart size={16}/> Contabilidad de todos los hoteles</Link>}
            </SidebarSection>
          )}

          {/* ═══ EMPRESA VYARA ═══ */}
          {(isAdmin || porModulo) && (
            <>
              {veEmpresa && <SidebarSection id="empresa" title="Empresa Vyara" icon={BarChart3} {...acordeon}>
              {ve('contabilidad_alcance') && sinRestriccion && <Link to="/admin/contabilidad-negocio?alcance=TODO" onClick={toggle} style={{...sidebarLink, color: '#003366', fontWeight: 800}}><BarChart3 size={16}/> Contabilidad consolidada</Link>}
              {ve('contabilidad_alcance') && sinRestriccion && <Link to="/admin/contabilidad-negocio?alcance=EMPRESA" onClick={toggle} style={sidebarLink}><PieChart size={16}/> Contabilidad de la empresa</Link>}
              {ve('nomina_alcance') && sinRestriccion && <Link to="/admin/nomina-negocio?alcance=EMPRESA" onClick={toggle} style={sidebarLink}><UserCheck size={16}/> Nómina de la empresa</Link>}
              {veAlguno(['proveedores', 'pagos_prov']) && <SidebarGroup title="PROVEEDORES" icon={Users}>
                <button
                  onClick={() => { openProveedorModal(); toggle(); }}
                  style={{...sidebarLink, background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer'}}
                >
                  <Plus size={16} color="#6366f1"/> Nuevo Proveedor
                </button>
                <Link to="/admin/gestion-proveedores" onClick={toggle} style={{...sidebarLink, fontWeight: 'bold', color: '#003366'}}>
                  <LayoutDashboard size={16}/> Panel Proveedores
                </Link>
              </SidebarGroup>}

              {veAlguno(['contabilidad', 'otros_ingresos', 'otros_gastos', 'analytics', 'mantenimiento']) && <SidebarGroup title="FINANZAS" icon={BarChart3}>
                {ve('analytics', true) && <Link to="/admin/analytics-hub" onClick={toggle} style={{...sidebarLink, color: '#7c3aed', fontWeight: 800}}><Activity size={16} color="#7c3aed"/> Analytics &amp; KPIs</Link>}
                {ve('analytics', true) && <Link to="/admin/revenue"    onClick={toggle} style={{...sidebarLink, color: '#059669', fontWeight: 800}}><TrendingUp size={16} color="#059669"/> Revenue Manager</Link>}
                {ve('contabilidad', true) && <Link to="/admin/ocr-recibos" onClick={toggle} style={sidebarLink}><Receipt size={16}/> OCR Recibos</Link>}
                {ve('mantenimiento', true) && <Link to="/admin/mantenimiento-preventivo" onClick={toggle} style={sidebarLink}><Wrench size={16}/> Mant. Preventivo</Link>}
                {ve('contabilidad', true) && <Link to="/admin/facturacion" onClick={toggle} style={{...sidebarLink, color: '#b45309', fontWeight: 800}}><Receipt size={16} color="#b45309"/> Facturación</Link>}
                {ve('contabilidad', true) && <Link to="/admin/contabilidad" onClick={toggle} style={{...sidebarLink, color: '#003366'}}><PieChart size={16}/> Resumen General</Link>}
                {ve('contabilidad', true) && <Link to="/admin/contabilidad" onClick={toggle} style={{...sidebarLink, color: '#2ecc71'}}><BarChart3 size={16}/> Dashboard Ingresos / Egresos</Link>}
                {ve('contabilidad', true) && <Link to="/admin/recibos-caja" onClick={toggle} style={{...sidebarLink, color: '#0369a1', fontWeight: 800}}><Receipt size={16} color="#0369a1"/> Recibos de Caja</Link>}
                {ve('otros_ingresos', true) && <Link to="/admin/otros-ingresos" onClick={toggle} style={{...sidebarLink, color: '#16a34a'}}><TrendingUp size={16}/> Otros Ingresos</Link>}
                {ve('otros_gastos', true) && <Link to="/admin/otros-gastos" onClick={toggle} style={{...sidebarLink, color: '#7c3aed'}}><TrendingDown size={16}/> Otros Gastos</Link>}
                {ve('contabilidad', true) && <Link to="/admin/distribuciones" onClick={toggle} style={{...sidebarLink, color: '#6366f1', fontWeight: 800}}><TrendingDown size={16}/> Distribución Utilidades</Link>}
              </SidebarGroup>}
              </SidebarSection>}

              {/* ═══ CONFIGURACIÓN ═══ */}
              <SidebarSection id="configuracion" title="Configuración" icon={SettingsIcon} {...acordeon}>
                {ve('julieta', true) && <Link to="/admin/julieta" onClick={toggle} style={{...sidebarLink, color: '#7c3aed', fontWeight: 800, background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 10, marginBottom: 4}}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>🤖 Julieta IA · Módulo</span>
                </Link>}
                <Link to="/admin/manual" onClick={toggle} style={{...sidebarLink, color: '#059669', fontWeight: 800}}><BookOpen size={16} color="#059669"/> Manual de Uso</Link>
                {ve('roles', true) && <Link to="/admin/roles" onClick={toggle} style={{...sidebarLink, color: '#003366', fontWeight: 800}}><Key size={16} color="#003366"/> Roles y Accesos</Link>}
                {ve('configuracion', true) && <Link to="/admin/configuracion" onClick={toggle} style={sidebarLink}><Users size={16}/> Colaboradores</Link>}
                {ve('configuracion', true) && <Link to="/admin/configuracion" onClick={toggle} style={sidebarLink}><SettingsIcon size={16}/> Ajustes Globales</Link>}
                {ve('configuracion', true) && <Link to="/admin/configuracion?tab=negocio" onClick={toggle} style={sidebarLink}><Briefcase size={16}/> Modelos de negocio</Link>}
                {ve('backup', true) && <Link to="/admin/configuracion?tab=backup" onClick={toggle} style={{...sidebarLink, color: '#003366', fontWeight: 800}}><Key size={16} color="#003366"/> Backup</Link>}
                {ve('documentos', true) && <Link to="/admin/documentos-legales" onClick={toggle} style={sidebarLink}><SettingsIcon size={16}/> Documentos Legales</Link>}
                {userRole === 'SUPER_ADMIN' && (
                  <Link to="/admin/superadmin" onClick={toggle} style={{...sidebarLink, color: '#c53030', fontWeight: 800}}>
                    <SettingsIcon size={16} color="#c53030"/> ⚙️ Super Admin
                  </Link>
                )}
              </SidebarSection>
            </>
          )}

          <div style={{ marginTop: 'auto', paddingTop: '20px', borderTop: '1px solid #eee' }}>
            <button onClick={() => { onLogout(); toggle(); }} style={sidebarLogoutBtn}>
              <LogOut size={18}/> Cerrar Sesión
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// --- MODAL PARA REGISTRO DE PROVEEDORES ---
const ModalProveedorRegistro = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div style={{...overlayStyle, zIndex: 8999, display: 'flex', justifyContent: 'center', alignItems: 'center'}} onClick={onClose}>
      <div 
        style={{...loginCard, width: '90%', maxWidth: '950px', maxHeight: '90vh', overflowY: 'auto', position: 'relative', zIndex: 9000}} 
        onClick={e => e.stopPropagation()}
      >
          <button 
            onClick={onClose} 
            style={{position: 'absolute', right: '25px', top: '25px', border: 'none', background: '#f1f5f9', borderRadius: '50%', padding: '8px', cursor: 'pointer', zIndex: 10}}
          >
            <X size={20} />
          </button>
          <div style={{padding: '20px'}}>
              <Suspense fallback={cargandoPantalla}><Proveedores soloFormulario={true} viewMode="formulario" onSuccess={onClose} /></Suspense>
          </div>
      </div>
    </div>
  );
};

// --- LOGIN ---
const GOOGLE_ICON = (
  <svg width="18" height="18" viewBox="0 0 48 48" style={{ flexShrink: 0 }}>
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    <path fill="none" d="M0 0h48v48H0z"/>
  </svg>
);

// Pantalla protegida sin sesión: se pide la clave ahí mismo, sin cambiar la dirección
// (si la dirección cambiara a /login, las pestañas sincronizadas entre dispositivos arrastrarían a la sesión abierta en el otro).
const SinAcceso = ({ auth, onLogin }) => (auth?.isAuth ? <Navigate to="/login" /> : <Login onLogin={onLogin} enSitio />);

const Login = ({ onLogin, enSitio = false, auth = null, onLogout = null }) => {
  const [user, setUser]     = useState('');
  const [pass, setPass]     = useState('');
  // Tipo de acceso: null (elegir) | 'cliente' | 'propietario' | 'hoteles' | 'empresa'. Se recuerda el último usado.
  const ROL_DE_GRUPO = { cliente: 'cliente', propietario: 'propietario', hoteles: 'administrador', empresa: 'administrador' };
  const grupoRecordado = () => { try { const g = localStorage.getItem('loginGrupo'); return !enSitio && ROL_DE_GRUPO[g] ? g : null; } catch { return null; } };
  const [accessGroup, setAccessGroup] = useState(grupoRecordado);
  const [role, setRole]     = useState(() => ROL_DE_GRUPO[grupoRecordado()] || 'administrador');
  const elegirGrupo = (grupo) => {
    setAccessGroup(grupo); setRole(ROL_DE_GRUPO[grupo] || 'administrador'); setUser(''); setPass('');
    try { grupo ? localStorage.setItem('loginGrupo', grupo) : localStorage.removeItem('loginGrupo'); } catch {}
  };
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [twoFAStep, setTwoFAStep]   = useState(false);
  const [twoFACode, setTwoFACode]   = useState('');
  const [twoFAUser, setTwoFAUser]   = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  const API = process.env.REACT_APP_API_URL || window.__APP_API_BASE__ || 'http://localhost:4000';

  const params      = new URLSearchParams(window.location.search);
  const oauthError  = params.get('error');
  const verifiedParam = params.get('verified'); // 'ok' | 'error' | null

  // Login mostrado dentro de una pantalla del panel (sin cambiar la dirección): quien puede ver todo se queda ahí
  const seQuedaEnSitio = (serverRole) => enSitio && ['ADMIN', 'SUPER_ADMIN'].includes((serverRole || '').toUpperCase());

  const roleRedirect = (serverRole, serverPermissions) => {
    const r = (serverRole || '').toUpperCase();
    if (r === 'CLIENT')       return '/';
    if (r === 'OWNER')        return '/propietario/perfil';
    if (r === 'VENDEDOR')     return '/vendedor/portal';
    if (r === 'PROVEEDOR')    return '/proveedor/portal';

    let perms = null;
    try { perms = serverPermissions ? JSON.parse(serverPermissions) : null; } catch {}

    // Con permisos granulares guardados: aterrizar en el primer módulo al que
    // realmente tenga acceso, en vez de asumirlo por el rol base.
    if (perms && !perms._superadmin) {
      if (perms.dashboard)     return '/admin/dashboard';
      if (perms.calendario)    return '/admin/calendario';
      if (perms.reservas)      return '/admin/reservas';
      if (perms.reservas_exp)  return '/admin/reservas-experiencias';
      if (perms.propiedades)   return '/admin/propiedades-lista';
      if (perms.experiencias)  return '/admin/experiencias-lista';
      if (perms.camareras)     return '/admin/camareras';
      if (perms.mantenimiento) return '/admin/mantenimiento';
      const hotel = RUTAS_HOTEL.find(([k]) => perms[k]);
      if (hotel) return hotel[1];
      return '/admin/dashboard';
    }

    // Sin permisos granulares guardados: comportamiento legacy por rol
    if (r === 'EDITOR_PROPS') return '/admin/propiedades-lista';
    if (r === 'EDITOR_EXPS')  return '/admin/experiencias-lista';
    if (r === 'CAMARERA') return '/admin/camareras';
    if (r === 'SUPERVISORA') return '/admin/camareras';
    if (r === 'MANTENIMIENTO') return '/admin/mantenimiento';
    if (r === 'VENDEDOR_INTERNO') return '/admin/calendario';
    if (PERMISOS_POR_DEFECTO[r]) { const hotel = RUTAS_HOTEL.find(([k]) => PERMISOS_POR_DEFECTO[r][k]); if (hotel) return hotel[1]; }
    return '/admin/dashboard';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const endpoint =
      role === 'propietario' ? '/api/auth/login-propietario' :
      role === 'cliente'     ? '/api/auth/login-cliente'     :
                               '/api/auth/login';
    try {
      const res = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: user, password: pass }),
      });
      let data = {};
      try { data = await res.json(); } catch {}
      if (res.ok && data.requires2FA) {
        // Servidor pide el código del autenticador
        setTwoFAUser(data.username);
        setTwoFAStep(true);
        setLoading(false);
        return;
      }
      if (res.ok) {
        const authData = {
          isAuth: true, loginAt: Date.now(),
          role: (data.role || '').toUpperCase(),
          username: data.username,
          clienteId: data.clienteId,
          displayName: data.displayName,
          ownerId: data.ownerId || null,
          permissions: data.permissions || null,
          appToken: data.appToken || null,
        };
        if (data.saToken) localStorage.setItem('saToken', data.saToken);
        onLogin(authData);
        localStorage.setItem('auth', JSON.stringify(authData));
        const dest =
          role === 'propietario' ? '/propietario/perfil' :
          role === 'cliente'     ? '/'                   :
                                   roleRedirect(data.role, data.permissions);
        if (!seQuedaEnSitio(data.role)) navigate(dest);
      } else if (res.status === 429) {
        Swal.fire({ icon: 'warning', title: 'Demasiados intentos', text: 'Has superado el límite. Espera 15 minutos e intenta de nuevo.' });
      } else {
        Swal.fire({ icon: 'error', title: 'Acceso denegado', text: data.error || 'Usuario o contraseña incorrectos.' });
      }
    } catch {
      Swal.fire({ icon: 'warning', title: 'Sin conexión', text: 'No se pudo conectar con el servidor.' });
    } finally { setLoading(false); }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    if (!twoFACode.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/2fa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: twoFAUser, code: twoFACode.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        const authData = {
          isAuth: true, loginAt: Date.now(),
          role: (data.role || '').toUpperCase(),
          username: data.username,
          clienteId: data.clienteId,
          displayName: data.displayName,
          ownerId: data.ownerId || null,
          permissions: data.permissions || null,
          appToken: data.appToken || null,
        };
        if (data.saToken) localStorage.setItem('saToken', data.saToken);
        onLogin(authData);
        localStorage.setItem('auth', JSON.stringify(authData));
        if (!seQuedaEnSitio(data.role)) navigate(roleRedirect(data.role, data.permissions));
      } else {
        Swal.fire({ icon: 'error', title: 'Código incorrecto', text: data.error || 'El código es inválido o expiró. Inténtalo de nuevo.' });
        setTwoFACode('');
      }
    } catch {
      Swal.fire({ icon: 'warning', title: 'Sin conexión', text: 'No se pudo conectar con el servidor.' });
    } finally { setLoading(false); }
  };

  const roles = [
    { key: 'administrador', label: 'Administrador', icon: '⚙️' },
    { key: 'propietario',   label: 'Propietario',   icon: '🏠' },
    { key: 'cliente',       label: 'Huésped',        icon: '👤' },
    { key: 'vendedor',      label: 'Vendedor',       icon: '🤝' },
    { key: 'proveedor',     label: 'Proveedor',      icon: '🧰' },
    { key: 'colaborador',   label: 'Colaborador',    icon: '🧹' },
  ];

  const hint = {
    administrador: 'Acceso al panel de gestión y operaciones',
    propietario:   'Portal de propietarios — estado de cuenta e informes',
    cliente:       'Consulta tus reservas y gestiona tu perfil',
    vendedor:      'Portal de vendedor aliado externo — reservas y comisiones',
    proveedor:     'Portal de proveedores — gestiona tus servicios y reservas',
    colaborador:   'Camareras y trabajadores de Vyara (apartamentos)',
  };

  // Sesión ya iniciada en este navegador: no se vuelve a pedir la clave
  if (!enSitio && auth?.isAuth && !loading && !twoFAStep) {
    const destino = roleRedirect(auth.role, auth.permissions);
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#f1f5f9', fontFamily: 'Inter, sans-serif', padding: 20 }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: 32, maxWidth: 400, width: '100%', textAlign: 'center', boxShadow: '0 10px 30px rgba(15,23,42,.08)' }}>
          <h2 style={{ margin: '0 0 8px', color: '#0f172a', fontSize: 22 }}>Ya iniciaste sesión</h2>
          <p style={{ margin: '0 0 22px', color: '#475569', fontSize: 15 }}>Estás dentro como <b>{auth.displayName || auth.username}</b>.</p>
          <button onClick={() => navigate(destino)} style={{ width: '100%', padding: '13px 16px', borderRadius: 10, border: 'none', background: '#003366', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>Continuar</button>
          {onLogout && <button onClick={onLogout} style={{ width: '100%', marginTop: 10, padding: '12px 16px', borderRadius: 10, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>Entrar con otra cuenta</button>}
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', fontFamily: 'Inter, sans-serif' }}>
      {/* ── Panel izquierdo — marca (oculto en móvil) ─────────────────────── */}
      <div style={{
        flex: 1, background: 'linear-gradient(155deg, #001a3a 0%, #003366 55%, #0a5499 100%)',
        display: isMobile ? 'none' : 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
        padding: '60px 48px', color: '#fff', minHeight: '100vh',
      }}>
        <div style={{ fontSize: 56, marginBottom: 20, filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.3))' }}>🌊</div>
        <h1 style={{ margin: '0 0 10px', fontSize: 34, fontWeight: 900, letterSpacing: '-1px', textAlign: 'center' }}>
          Vyara Group
        </h1>
        <p style={{ margin: '0 0 48px', fontSize: 15, color: 'rgba(255,255,255,0.65)', textAlign: 'center', maxWidth: 300 }}>
          Plataforma de gestión de alquileres vacacionales en Cartagena
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%', maxWidth: 300 }}>
          {[
            { icon: '🏠', text: 'Gestión de propiedades y reservas' },
            { icon: '📊', text: 'Estados de cuenta en tiempo real' },
            { icon: '🧹', text: 'Control de camareras y mantenimiento' },
            { icon: '💳', text: 'Pagos e ingresos automatizados' },
          ].map(({ icon, text }) => (
            <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, color: 'rgba(255,255,255,0.75)' }}>
              <span style={{ fontSize: 18 }}>{icon}</span> {text}
            </div>
          ))}
        </div>
      </div>

      {/* ── Panel derecho — formulario ─────────────────────────────────────── */}
      <div style={{
        width: isMobile ? '100%' : 440, background: '#fff', display: 'flex', flexDirection: 'column',
        justifyContent: 'center', padding: isMobile ? '40px 24px' : '60px 48px', overflowY: 'auto',
      }}>
        <div style={{ marginBottom: 36 }}>
          <h2 style={{ margin: '0 0 6px', fontSize: 26, fontWeight: 900, color: '#0f172a' }}>
            {twoFAStep ? 'Verificación en dos pasos' : 'Bienvenido'}
          </h2>
          <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>
            {twoFAStep
              ? 'Ingresa el código de 6 dígitos de tu aplicación autenticadora'
              : 'Selecciona tu tipo de acceso para continuar'}
          </p>
        </div>

        {/* Paso 2FA — reemplaza todo el formulario normal */}
        {twoFAStep && (
          <form onSubmit={handle2FASubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: '14px 16px', fontSize: 13, color: '#1d4ed8', textAlign: 'center' }}>
              Abre Google Authenticator (o similar) y copia el código de <strong>Vyara Group</strong>
            </div>
            <input
              type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
              placeholder="Código de 6 dígitos"
              value={twoFACode} onChange={e => setTwoFACode(e.target.value.replace(/\D/g, ''))}
              required autoFocus
              style={{ padding: '18px 16px', borderRadius: 12, border: '2px solid #003366', fontSize: 22, fontWeight: 800, letterSpacing: 8, textAlign: 'center', background: '#f8fafc', outline: 'none', width: '100%', boxSizing: 'border-box' }}
            />
            <button type="submit" disabled={loading || twoFACode.length < 6} style={{
              padding: 15, background: loading ? '#94a3b8' : '#003366', color: '#fff',
              border: 'none', borderRadius: 12, fontWeight: 800, fontSize: 15,
              cursor: (loading || twoFACode.length < 6) ? 'not-allowed' : 'pointer',
            }}>
              {loading ? 'Verificando...' : 'Verificar'}
            </button>
            <button type="button" onClick={() => { setTwoFAStep(false); setTwoFACode(''); }} style={{
              background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 13, textDecoration: 'underline',
            }}>
              Volver al inicio de sesión
            </button>
          </form>
        )}

        {/* Contenido normal — oculto durante el paso 2FA */}
        <div style={{ display: twoFAStep ? 'none' : 'block' }}>

        {!accessGroup ? (
          /* ── Paso 1: elegir el tipo de acceso ──────────────────────────── */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 8 }}>
            {[
              ['cliente', '👤', 'Soy huésped', 'Consulta tus reservas y gestiona tu perfil'],
              ['propietario', '🏠', 'Soy propietario', 'Estado de cuenta e informes de tu propiedad'],
              ['hoteles', '🏨', 'Hoteles', 'Personal del hotel: recepción, aseo y mantenimiento'],
              ['empresa', '🏢', 'Acceso empresa y proveedores', 'Administradores, vendedores aliados, proveedores y colaboradores'],
            ].map(([grupo, icono, titulo, detalle]) => (
              <button key={grupo} type="button" onClick={() => elegirGrupo(grupo)} style={{
                display: 'flex', alignItems: 'center', gap: 16, padding: '18px 20px', textAlign: 'left',
                border: '2px solid #e2e8f0', borderRadius: 16, background: '#fff', cursor: 'pointer',
                transition: 'all 0.15s', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              }}>
                <span style={{ fontSize: 28 }}>{icono}</span>
                <span>
                  <span style={{ display: 'block', fontWeight: 800, fontSize: 16, color: '#0f172a' }}>{titulo}</span>
                  <span style={{ display: 'block', fontSize: 13, color: '#64748b', marginTop: 2 }}>{detalle}</span>
                </span>
              </button>
            ))}
          </div>
        ) : (
          <>
          <button type="button" onClick={() => elegirGrupo(null)} style={{
            display: 'flex', alignItems: 'center', gap: 6, marginBottom: 18, padding: 0,
            border: 'none', background: 'none', color: '#64748b', fontWeight: 700, fontSize: 13, cursor: 'pointer',
          }}>
            ← Cambiar tipo de acceso
          </button>

          {accessGroup === 'empresa' && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 28, background: '#f1f5f9', borderRadius: 14, padding: 4 }}>
              {roles.filter(r => r.key !== 'cliente' && r.key !== 'propietario').map(r => (
                <button key={r.key} type="button" onClick={() => {
                  setRole(r.key);
                }} style={{
                  flex: 1, padding: '10px 6px', border: 'none', borderRadius: 10, cursor: 'pointer',
                  background: role === r.key ? '#003366' : 'transparent',
                  color: role === r.key ? '#fff' : '#64748b',
                  fontWeight: 700, fontSize: 11, transition: 'all 0.15s',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                }}>
                  <span style={{ fontSize: 16 }}>{r.icon}</span>
                  {r.label}
                </button>
              ))}
            </div>
          )}

          {accessGroup === 'hoteles' && (
            <Suspense fallback={null}><IngresoHoteles API={API} iconoGoogle={GOOGLE_ICON} /></Suspense>
          )}

          <p style={{ margin: '0 0 20px', fontSize: 12, color: '#64748b', fontWeight: 600, textAlign: 'center' }}>
            {accessGroup === 'hoteles' ? 'Recepción y administración del hotel: entra con tu usuario y contraseña' : hint[role]}
          </p>

        {verifiedParam === 'ok' && (
          <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: '12px 14px', fontSize: 13, color: '#15803d', textAlign: 'center', marginBottom: 16, fontWeight: 600 }}>
            ✅ Correo verificado. Ya puedes ingresar.
          </div>
        )}
        {verifiedParam === 'error' && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 14px', fontSize: 13, color: '#dc2626', textAlign: 'center', marginBottom: 16 }}>
            El enlace de verificación expiró o ya fue usado.
          </div>
        )}
        {oauthError && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#dc2626', textAlign: 'center', marginBottom: 16 }}>
            {oauthError === 'google_no_configurado'
              ? 'Google Login no configurado. Contacta al administrador.'
              : 'No se pudo iniciar sesión con Google. Inténtalo de nuevo.'}
          </div>
        )}

        {/* Administrador: usuario + contraseña */}
        {role === 'administrador' && (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <input
              type="text" placeholder="Usuario" value={user}
              onChange={e => setUser(e.target.value)} required autoComplete="username"
              style={{ padding: '14px 16px', borderRadius: 12, border: '1.5px solid #e2e8f0', fontSize: 14, background: '#f8fafc', outline: 'none', boxSizing: 'border-box', width: '100%' }}
            />
            <input
              type="password" placeholder="Contraseña" value={pass}
              onChange={e => setPass(e.target.value)} required autoComplete="current-password"
              style={{ padding: '14px 16px', borderRadius: 12, border: '1.5px solid #e2e8f0', fontSize: 14, background: '#f8fafc', outline: 'none', boxSizing: 'border-box', width: '100%' }}
            />
            <button type="submit" disabled={loading} style={{
              padding: '15px', background: loading ? '#94a3b8' : '#003366', color: '#fff',
              border: 'none', borderRadius: 12, fontWeight: 800, fontSize: 15,
              cursor: loading ? 'not-allowed' : 'pointer', transition: 'background 0.15s',
            }}>
              {loading ? 'Verificando...' : 'Ingresar'}
            </button>
            {accessGroup !== 'hoteles' && (<>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
              <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>o</span>
              <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
            </div>
            <button
              type="button"
              onClick={() => { window.location.href = `${API}/api/auth/google`; }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 13, width: '100%', background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12, cursor: 'pointer', fontWeight: 700, fontSize: 14, color: '#1e293b', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', boxSizing: 'border-box' }}
            >
              {GOOGLE_ICON} Continuar con Google
            </button>
                      </>)}
          </form>
        )}

        {/* Propietario: formulario usuario/contraseña + Google */}
        {role === 'propietario' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <input
                type="text" placeholder="Usuario" value={user}
                onChange={e => setUser(e.target.value)} required autoComplete="username"
                style={{ padding: '14px 16px', borderRadius: 12, border: '1.5px solid #e2e8f0', fontSize: 14, background: '#f8fafc', outline: 'none', boxSizing: 'border-box', width: '100%' }}
              />
              <input
                type="password" placeholder="Contraseña" value={pass}
                onChange={e => setPass(e.target.value)} required autoComplete="current-password"
                style={{ padding: '14px 16px', borderRadius: 12, border: '1.5px solid #e2e8f0', fontSize: 14, background: '#f8fafc', outline: 'none', boxSizing: 'border-box', width: '100%' }}
              />
              <button type="submit" disabled={loading} style={{
                padding: '15px', background: loading ? '#94a3b8' : '#003366', color: '#fff',
                border: 'none', borderRadius: 12, fontWeight: 800, fontSize: 15,
                cursor: loading ? 'not-allowed' : 'pointer', transition: 'background 0.15s',
              }}>
                {loading ? 'Verificando...' : 'Ingresar'}
              </button>
            </form>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
              <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>o</span>
              <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
            </div>
            <button
              type="button"
              onClick={() => {
                sessionStorage.setItem('propietarioGoogleIntent', 'register');
                window.location.href = `${API}/api/auth/google?state=propietario`;
              }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '14px', width: '100%', background: '#fff', border: '2px solid #e2e8f0', borderRadius: 12, cursor: 'pointer', fontWeight: 700, fontSize: 14, color: '#1e293b', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', boxSizing: 'border-box' }}
            >
              {GOOGLE_ICON} Continuar con Google
            </button>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
              ¿No tienes cuenta?{' '}
              <a href="/registro-propietario" style={{ color: '#003366', fontWeight: 700 }}>Regístrate gratis</a>
            </p>
          </div>
        )}

        {/* Vendedor Aliado: usuario + contraseña */}
        {role === 'colaborador' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[['/camarera/login', 'Soy camarera', 'Mis tareas de aseo, pagos y perfil'], ['/trabajador/login', 'Soy trabajador', 'Mantenimiento y tareas del día']].map(([ruta, titulo, detalle]) => (
              <button key={ruta} type="button" onClick={() => navigate(ruta)} style={{ display: 'block', textAlign: 'left', padding: '16px 18px', border: '2px solid #e2e8f0', borderRadius: 14, background: '#fff', cursor: 'pointer' }}>
                <span style={{ display: 'block', fontWeight: 800, fontSize: 15, color: '#0f172a' }}>{titulo}</span>
                <span style={{ display: 'block', fontSize: 13, color: '#64748b', marginTop: 2 }}>{detalle} · entras con Google</span>
              </button>
            ))}
            <p style={{ margin: 0, fontSize: 12.5, color: '#64748b', textAlign: 'center' }}>¿Trabajas en un hotel? Vuelve y elige «Hoteles».</p>
          </div>
        )}

        {role === 'vendedor' && (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <input
              type="text" placeholder="Usuario" value={user}
              onChange={e => setUser(e.target.value)} required autoComplete="username"
              style={{ padding: '14px 16px', borderRadius: 12, border: '1.5px solid #e2e8f0', fontSize: 14, background: '#f8fafc', outline: 'none', boxSizing: 'border-box', width: '100%' }}
            />
            <input
              type="password" placeholder="Contraseña" value={pass}
              onChange={e => setPass(e.target.value)} required autoComplete="current-password"
              style={{ padding: '14px 16px', borderRadius: 12, border: '1.5px solid #e2e8f0', fontSize: 14, background: '#f8fafc', outline: 'none', boxSizing: 'border-box', width: '100%' }}
            />
            <button type="submit" disabled={loading} style={{
              padding: '15px', background: loading ? '#94a3b8' : '#003366', color: '#fff',
              border: 'none', borderRadius: 12, fontWeight: 800, fontSize: 15,
              cursor: loading ? 'not-allowed' : 'pointer', transition: 'background 0.15s',
            }}>
              {loading ? 'Verificando...' : 'Ingresar'}
            </button>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
              ¿No tienes cuenta?{' '}
              <a href="/registro-aliado" style={{ color: '#003366', fontWeight: 700 }}>Regístrate como aliado</a>
            </p>
          </form>
        )}

        {/* Proveedor: usuario + contraseña + Google */}
        {role === 'proveedor' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <button
              type="button"
              onClick={() => { window.location.href = `${API}/api/auth/google?state=proveedor`; }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '14px', width: '100%', background: '#fff', border: '2px solid #e2e8f0', borderRadius: 12, cursor: 'pointer', fontWeight: 800, fontSize: 14, color: '#1e293b', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', boxSizing: 'border-box' }}
            >
              {GOOGLE_ICON} Continuar con Google
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
              <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>o con usuario y contraseña</span>
              <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
            </div>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <input
                type="text" placeholder="Usuario" value={user}
                onChange={e => setUser(e.target.value)} required autoComplete="username"
                style={{ padding: '14px 16px', borderRadius: 12, border: '1.5px solid #e2e8f0', fontSize: 14, background: '#f8fafc', outline: 'none', boxSizing: 'border-box', width: '100%' }}
              />
              <input
                type="password" placeholder="Contraseña" value={pass}
                onChange={e => setPass(e.target.value)} required autoComplete="current-password"
                style={{ padding: '14px 16px', borderRadius: 12, border: '1.5px solid #e2e8f0', fontSize: 14, background: '#f8fafc', outline: 'none', boxSizing: 'border-box', width: '100%' }}
              />
              <button type="submit" disabled={loading} style={{
                padding: '15px', background: loading ? '#94a3b8' : '#003366', color: '#fff',
                border: 'none', borderRadius: 12, fontWeight: 800, fontSize: 15,
                cursor: loading ? 'not-allowed' : 'pointer', transition: 'background 0.15s',
              }}>
                {loading ? 'Verificando...' : 'Ingresar'}
              </button>
            </form>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
              ¿No tienes cuenta?{' '}
              <a href="/registro-proveedor" style={{ color: '#003366', fontWeight: 700 }}>Regístrate como proveedor</a>
            </p>
          </div>
        )}

        {/* Huésped: solo Google */}
        {role === 'cliente' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <button
              type="button"
              onClick={() => { window.location.href = `${API}/api/auth/google`; }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '16px', width: '100%', background: '#fff', border: '2px solid #e2e8f0', borderRadius: 12, cursor: 'pointer', fontWeight: 800, fontSize: 15, color: '#1e293b', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', boxSizing: 'border-box' }}
            >
              {GOOGLE_ICON} Continuar con Google
            </button>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
              ¿No tienes cuenta?{' '}
              <a href="/registro" style={{ color: '#003366', fontWeight: 700 }}>Regístrate gratis</a>
            </p>
          </div>
        )}
          </>
        )}

        </div>{/* fin bloque oculto durante 2FA */}

        {/* Portal de Aliados — solo visible en tab de admin o pantalla inicial */}
        {(!accessGroup || role === 'administrador') && <div style={{ marginTop: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
            <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, whiteSpace: 'nowrap' }}>Equipo operativo</span>
            <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
          </div>
          <a href="/trabajador/login" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            padding: '14px 20px', background: '#f0fdf4', border: '2px solid #22c55e',
            borderRadius: 12, color: '#15803d', fontWeight: 800, fontSize: 14,
            textDecoration: 'none', boxSizing: 'border-box', width: '100%',
          }}>
            🤝 Portal de Aliados
          </a>
        </div>}

        {/* Versión del sistema */}
        <div style={{ marginTop: 'auto', paddingTop: 32, textAlign: 'center' }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 11, color: '#cbd5e1', fontWeight: 600, letterSpacing: '0.5px',
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
            {`Vyara Group · v3.4.1 · ${process.env.REACT_APP_BUILD_DATE || ''}`}
          </span>
        </div>
      </div>
    </div>
  );
};

// --- APP PRINCIPAL ---
export default function App() {
  const API_BASE = process.env.REACT_APP_API_URL || window.__APP_API_BASE__ || 'http://localhost:4000';
  const [properties, setProperties] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [view, setView] = useState('apartamentos');
  const [globalSettings, setGlobalSettings] = useState({ businessName: '', logoUrl: '' });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isProvModalOpen, setIsProvModalOpen] = useState(false);
  const [adminUnreadMessages, setAdminUnreadMessages] = useState(0);
  const [ownerUnreadNotifs, setOwnerUnreadNotifs] = useState(0);
  const [legalDone, setLegalDone] = useState(false);
  const isMobile = useIsMobile();
  
  // ── Sonido de notificación (Web Audio API) ──────────────────────────────
  const playNotifSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      [880, 1100].forEach((freq, i) => {
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.frequency.value = freq; osc.type = 'sine';
        const t = ctx.currentTime + i * 0.18;
        gain.gain.setValueAtTime(0.25, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
        osc.start(t); osc.stop(t + 0.35);
      });
    } catch {}
  };

  const SESSION_MS = 8 * 60 * 60 * 1000; // 8 horas

  // Auth de trabajador (portal separado, no usa el auth principal)
  const [trabAuth, setTrabAuth] = useState(() => {
    try {
      const p = JSON.parse(localStorage.getItem('trabAuth') || '');
      return (p.activo && p.statusTrabajador === 'activo') ? p : null;
    } catch { return null; }
  });

  useEffect(() => {
    const sync = () => {
      try {
        const p = JSON.parse(localStorage.getItem('trabAuth') || '');
        setTrabAuth((p.activo && p.statusTrabajador === 'activo') ? p : null);
      } catch { setTrabAuth(null); }
    };
    window.addEventListener('trabAuthChanged', sync);
    window.addEventListener('storage', sync);
    return () => { window.removeEventListener('trabAuthChanged', sync); window.removeEventListener('storage', sync); };
  }, []);

  const [auth, setAuth] = useState(() => {
    // Leer googleAuth de la URL de forma SINCRÓNICA antes de que los route guards se evalúen.
    // Si no lo hacemos aquí, el guard redirige a /login antes de que el useEffect lo procese.
    const urlParams = new URLSearchParams(window.location.search);
    const googleAuthParam = urlParams.get('googleAuth');
    if (googleAuthParam) {
      try {
        const authData = JSON.parse(decodeURIComponent(googleAuthParam));
        // Portales de personal: se guarda antes de que cargue la pantalla de ingreso, que ya lo necesita
        if (authData?.portalToken) localStorage.setItem('portalToken', authData.portalToken);
        if (authData && authData.isAuth && authData.role) {
          const normalizedAuth = { ...authData, loginAt: Date.now() };
          localStorage.setItem('auth', JSON.stringify(normalizedAuth));
          return normalizedAuth;
        }
      } catch { /* malformed — fall through */ }
    }

    const saved = localStorage.getItem('auth');
    if (!saved) return { isAuth: false, role: null, username: '' };
    try {
      const parsed = JSON.parse(saved);
      // Expiración de sesión
      if (parsed.loginAt && Date.now() - parsed.loginAt > SESSION_MS) {
        localStorage.removeItem('auth');
        return { isAuth: false, role: null, username: '' };
      }
      return parsed;
    } catch { return { isAuth: false, role: null, username: '' }; }
  });

  const fetchData = async () => {
    try {
      const [resProp, resExp, resSet] = await Promise.all([
        fetch(`${API_BASE}/api/properties?all=true&publicOnly=true`),
        fetch(`${API_BASE}/api/experiences`),
        fetch(`${API_BASE}/api/settings`)
      ]);
      const propData = await resProp.json();
      setProperties(Array.isArray(propData) ? propData : (propData.data || []));
      const expData = await resExp.json();
      setExperiences(Array.isArray(expData) ? expData : (expData.data || []));
      const dataS = await resSet.json();
      if(dataS) setGlobalSettings(dataS);
    } catch (err) { console.error("Error:", err); }
  };

  useEffect(() => { fetchData(); }, []);

  // ─── Auto-actualización: detecta nuevo deploy y muestra banner ───────────────
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  useEffect(() => {
    const currentVersion = window.__BUILD_VERSION__;
    if (!currentVersion) return; // solo en producción (el HTML inyecta esta variable)

    const check = async () => {
      try {
        const r = await fetch(`${API_BASE}/api/version`, { cache: 'no-store' });
        if (!r.ok) return;
        const { version } = await r.json();
        if (version && version !== currentVersion) setNewVersionAvailable(true);
      } catch {}
    };

    // Verificar al enfocar la pestaña
    const onVisible = () => { if (!document.hidden) check(); };
    document.addEventListener('visibilitychange', onVisible);

    // Verificar cada 5 minutos
    const iv = setInterval(check, 5 * 60 * 1000);

    return () => { document.removeEventListener('visibilitychange', onVisible); clearInterval(iv); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if ((auth.role || '').toUpperCase() !== 'ADMIN') {
      setAdminUnreadMessages(0);
      return;
    }
    let cancelled = false;
    const loadUnread = async () => {
      try {
        const r = await fetch(`${API_BASE}/api/messages/stats`);
        const d = await r.json();
        if (!cancelled) setAdminUnreadMessages(Number(d.unread || 0));
      } catch {}
    };
    loadUnread();
    const iv = setInterval(loadUnread, 15000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [auth.role]);

  // ─── Notificaciones del propietario (OWNER) ───────────────────────────────
  useEffect(() => {
    const role = (auth.role || '').toUpperCase();
    if (!auth.isAuth || role !== 'OWNER') { setOwnerUnreadNotifs(0); return; }
    let cancelled = false;
    const loadOwnerNotifs = async () => {
      try {
        const token = auth.appToken;
        const r = await fetch(`${API_BASE}/api/owner-notifications`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled && r.ok) {
          const d = await r.json();
          setOwnerUnreadNotifs(d.unread || 0);
        }
      } catch {}
    };
    loadOwnerNotifs();
    const iv = setInterval(loadOwnerNotifs, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [auth.isAuth, auth.role, auth.appToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Notificaciones de admin ───────────────────────────────────────────────
  const [notifications, setNotifications]       = useState([]);
  const [showNotifDrop, setShowNotifDrop]       = useState(false);
  const [lastSeenNotif, setLastSeenNotif]       = useState(() => localStorage.getItem('lastSeenNotif') || '');
  const isAdmin = ['ADMIN', 'SUPER_ADMIN'].includes((auth.role || '').toUpperCase());

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    const load = async () => {
      try {
        const token = (() => { try { return JSON.parse(localStorage.getItem('auth') || '{}').appToken; } catch { return null; } })();
        const r = await fetch(`${API_BASE}/api/notifications/recent`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        const d = await r.json();
        if (!cancelled && Array.isArray(d)) setNotifications(d);
      } catch {}
    };
    load();
    const iv = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [isAdmin]);

  const unreadNotifCount = notifications.filter(n => !lastSeenNotif || new Date(n.timestamp) > new Date(lastSeenNotif)).length;

  const markNotifsRead = () => {
    const now = new Date().toISOString();
    setLastSeenNotif(now);
    localStorage.setItem('lastSeenNotif', now);
  };

  const fmtNotifTime = (ts) => {
    const diff = Math.floor((Date.now() - new Date(ts)) / 60000);
    if (diff < 1) return 'ahora';
    if (diff < 60) return `hace ${diff}m`;
    if (diff < 1440) return `hace ${Math.floor(diff / 60)}h`;
    return `hace ${Math.floor(diff / 1440)}d`;
  };

  // ─── Web Push subscription ────────────────────────────────────────────────
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  useEffect(() => {
    if (!isAdmin || !('serviceWorker' in navigator) || !('PushManager' in window)) return;
    navigator.serviceWorker.ready.then(reg => {
      reg.pushManager.getSubscription().then(sub => setPushEnabled(!!sub));
    }).catch(() => {});
  }, [isAdmin]);

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
  };

  const togglePush = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      alert('Tu navegador no soporta notificaciones push. En iPhone, agrega esta app a la pantalla de inicio primero.');
      return;
    }
    setPushLoading(true);
    try {
      const token = (() => { try { return JSON.parse(localStorage.getItem('auth') || '{}').appToken; } catch { return null; } })();
      const reg = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      if (pushEnabled) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await fetch(`${API_BASE}/api/push/unsubscribe`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify({ endpoint: sub.endpoint }),
          });
          await sub.unsubscribe();
        }
        setPushEnabled(false);
      } else {
        const perm = await Notification.requestPermission();
        if (perm !== 'granted') { setPushLoading(false); return; }
        const keyRes = await fetch(`${API_BASE}/api/push/vapid-public`);
        const { publicKey } = await keyRes.json();
        const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) });
        const json = sub.toJSON();
        await fetch(`${API_BASE}/api/push/subscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ endpoint: json.endpoint, keys: json.keys, userAgent: navigator.userAgent }),
        });
        setPushEnabled(true);
      }
    } catch (e) {
      console.error('[Push]', e);
    }
    setPushLoading(false);
  };

  // Procesa el resultado del callback de Google OAuth
  // El auth ya fue inicializado en el useState() de forma sincrónica.
  // Este efecto solo limpia la URL y redirige al destino correcto.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const googleAuth = params.get('googleAuth');
    if (!googleAuth) return;
    try {
      const authData = JSON.parse(decodeURIComponent(googleAuth));
      // Limpiar sessionStorage de intent propietario
      sessionStorage.removeItem('propietarioGoogleIntent');
      // Limpiar el parámetro de la URL
      window.history.replaceState({}, '', window.location.pathname);
      // Redirigir según rol (auth ya está guardado — solo navegamos)
      const role = (authData.role || '').toUpperCase();
      if (role === 'OWNER') {
        window.location.replace('/propietario/perfil');
      } else if (role === 'CLIENT') {
        // Quien inició sesión a mitad de una reserva vuelve a ese punto (lo guarda la pantalla de reserva del hotel)
        let volver = null;
        try { volver = sessionStorage.getItem('volverTrasLogin'); sessionStorage.removeItem('volverTrasLogin'); } catch {}
        window.location.replace(volver && volver.startsWith('/reservar-hotel/') ? volver : '/');
      } else if (role === 'PROVEEDOR') {
        // Si viene con flag de completar registro → completar, si no → portal directo
        if (window.location.pathname !== '/proveedor/completar-registro') {
          window.location.replace('/proveedor/portal');
        }
      }
      // Admin y otros roles se quedan donde están (ruta normal de login)
    } catch { /* malformed */ }
  }, []);

  // Vyara 2.0: el apartado "Hoteles" del menú público aparece cuando hay hoteles publicados
  const [hayHoteles, setHayHoteles] = useState(false);
  useEffect(() => { cargarHoteles().then(h => setHayHoteles(h.length > 0)); }, []);

  const handleLogout = () => {
    setAuth({ isAuth: false, role: null, username: '' });
    localStorage.removeItem('auth');
    localStorage.removeItem('saToken');
    setLegalDone(false);
  };

  // ── Polling de notificaciones cada 30 s (solo admin/super_admin) ─────────
  useEffect(() => {
    const role = (auth.role || '').toUpperCase();
    if (!auth.isAuth || role === 'CLIENT' || role === 'OWNER') return;
    let prevUnread = null;
    const check = async () => {
      try {
        const r = await fetch(`${API_BASE}/api/messages/stats`);
        if (!r.ok) return;
        const d = await r.json();
        const cur = d.unread || 0;
        if (prevUnread !== null && cur > prevUnread) playNotifSound();
        prevUnread = cur;
        setAdminUnreadMessages(cur);
      } catch {}
    };
    check();
    const iv = setInterval(check, 30000);
    return () => clearInterval(iv);
  }, [auth.isAuth, auth.role]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sesión vencida o cambiada en otra pestaña ────────────────────────────
  // Se revisa cada 30 s y al volver a la pestaña (un temporizador largo no avanza con el equipo suspendido).
  useEffect(() => {
    const leerGuardada = () => { try { return JSON.parse(localStorage.getItem('auth') || 'null'); } catch { return null; } };
    const vencida = (a) => {
      if (!a?.isAuth) return true;
      if (a.loginAt && Date.now() - a.loginAt > SESSION_MS) return true;
      try {
        const cuerpo = String(a.appToken || '').split('.')[0];
        if (cuerpo) {
          const exp = JSON.parse(atob(cuerpo.replace(/-/g, '+').replace(/_/g, '/'))).exp;
          if (exp && Date.now() > exp) return true;
        }
      } catch {}
      return false;
    };
    const revisar = (cerradaEnOtra = false) => {
      const guardada = leerGuardada();
      if (guardada?.isAuth && !vencida(guardada)) {
        if (guardada.loginAt !== auth.loginAt || guardada.username !== auth.username) setAuth(guardada);
        return;
      }
      if (auth.isAuth && (cerradaEnOtra || vencida(auth))) {
        setAuth({ isAuth: false, role: null, username: '' });
        if (guardada) { localStorage.removeItem('auth'); localStorage.removeItem('saToken'); }
        setLegalDone(false);
      }
    };
    const alVolver = () => { if (!document.hidden) revisar(); };
    const alCambiar = (e) => { if (e.key === 'auth') revisar(!e.newValue); };
    revisar();
    const iv = setInterval(() => revisar(), 30000);
    document.addEventListener('visibilitychange', alVolver);
    const alEnfocar = () => revisar();
    window.addEventListener('focus', alEnfocar);
    window.addEventListener('storage', alCambiar);
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', alVolver); window.removeEventListener('focus', alEnfocar); window.removeEventListener('storage', alCambiar); };
  }, [auth.isAuth, auth.loginAt, auth.username]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Meta Pixel — inyección dinámica ──────────────────────────────────────
  useEffect(() => {
    fetch(`${API_BASE}/api/integrations`)
      .then(r => r.json())
      .then(data => {
        const pixelId = data?.META_PIXEL_ID?.value || '';
        if (!pixelId || document.getElementById('meta-pixel-script')) return;
        // Base script
        const script = document.createElement('script');
        script.id = 'meta-pixel-script';
        script.innerHTML = `
          !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '${pixelId}');
          fbq('track', 'PageView');
        `;
        document.head.appendChild(script);
        // noscript img
        const noscript = document.createElement('noscript');
        noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1"/>`;
        document.head.appendChild(noscript);
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Mapa: módulo legacy → lista de permission-keys nuevas
  const PERM_MAP = {
    'admin':  ['configuracion','roles','documentos','mercado','julieta','mensajes','estados_cuenta','contabilidad','otros_ingresos','otros_gastos','nomina','pagos_prov','pagos_nomina','propietarios','conciliacion_exp','fidelizacion'],
    'props':  ['propiedades','reservas','calendario','camareras','mantenimiento','propietarios'],
    'exps':   ['experiencias','reservas_exp','proveedores'],
  };

  // `key` es el módulo exacto de "Roles y Accesos"; solo lo usan los roles por módulo
  const hasAccess = (module, key) => {
    if (!auth.isAuth || !auth.role) return false;
    const role = (auth.role || '').toUpperCase();
    if (role === 'SUPER_ADMIN') return true;

    let perms = null;
    try { perms = auth.permissions ? JSON.parse(auth.permissions) : null; } catch {}

    if (ROLES_POR_MODULO.includes(role)) {
      const activos = perms || PERMISOS_POR_DEFECTO[role] || {};
      if (key === '*') return true;
      return !!activos[key || module];
    }

    // Sin permisos guardados → comportamiento legacy por rol
    if (!perms) {
      if (role === 'ADMIN') return true;
      if (module === 'props' && role === 'EDITOR_PROPS') return true;
      if (module === 'exps' && role === 'EDITOR_EXPS') return true;
      if (module === 'exps' && role === 'CAMARERA') return false;
      return false;
    }

    if (perms._superadmin) return true;

    const keys = PERM_MAP[module] || [module];
    return keys.some(k => !!perms[k]);
  };

  // Espacio de cada hotel: los módulos que la persona puede abrir y la pantalla de cada uno
  const modulosHotel = modulosVisibles(k => hasAccess('admin', k), auth.role);
  const veHotelesRuta = modulosHotel.length > 1;
  const PANTALLAS_HOTEL = {
    tablero: <HotelTablero />, recepcion: <HotelRecepcion />, aseo: <HotelAseo />, restaurante: <HotelRestaurante />, comidas: <HotelComidas />,
    mantenimiento: <HotelMantenimiento />, proveedores: <HotelProveedores />, compras: <HotelCompras />, espacios: <HotelEspacios />, comercial: <HotelComercial />, cierres: <HotelCierres />,
    usuarios: <HotelUsuarios />, configuracion: <HotelesAdmin />,
    nomina: <ConAlcanceHotel><NominaAlcance /></ConAlcanceHotel>, contabilidad: <ConAlcanceHotel><ContabilidadAlcance /></ConAlcanceHotel>,
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fff' }}>
      {/* Banner de nueva versión disponible */}
      {newVersionAvailable && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: '#003366', color: '#fff', borderRadius: 14, padding: '14px 22px',
          display: 'flex', alignItems: 'center', gap: 14, zIndex: 99999,
          boxShadow: '0 8px 32px rgba(0,0,0,0.25)', fontSize: 14, fontWeight: 700,
          whiteSpace: 'nowrap',
        }}>
          <span>🚀 Hay una nueva versión disponible</span>
          <button
            onClick={() => window.location.reload()}
            style={{ background: '#fff', color: '#003366', border: 'none', borderRadius: 8, padding: '7px 16px', fontWeight: 900, cursor: 'pointer', fontSize: 13 }}
          >
            Actualizar ahora
          </button>
          <button
            onClick={() => setNewVersionAvailable(false)}
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 2px' }}
          >
            ✕
          </button>
        </div>
      )}
      <style>
        {`
          *, *::before, *::after { box-sizing: border-box; }
          input::-webkit-outer-spin-button,
          input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
          input[type=number] { -moz-appearance: textfield; }
          .status-badge { padding: 4px 12px; border-radius: 20px; font-size: 11px; font-weight: 700; text-transform: uppercase; }
          .status-active { background-color: #e0f2fe; color: #0369a1; }
          .status-overdue { background-color: #fee2e2; color: #b91c1c; }

          /* ── RESPONSIVE GLOBAL ── */
          .resp-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
          .resp-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; border-radius: 10px; }
          .resp-table-wrap table { min-width: 600px; }

          @media (max-width: 639px) {
            /* Tablas — scroll horizontal */
            table { min-width: 520px; }
            .resp-table-wrap table { min-width: 480px; }

            /* Cards con grids — colapsar a 1 col */
            .resp-grid-1 { grid-template-columns: 1fr !important; }
            .resp-grid-2 { grid-template-columns: repeat(2, 1fr) !important; }

            /* Modales — ancho completo */
            .resp-modal { width: 95vw !important; max-width: 95vw !important; padding: 16px !important; }

            /* Textos grandes — reducir */
            .resp-title { font-size: 18px !important; }
            .resp-subtitle { font-size: 12px !important; }

            /* Botones acción — full width */
            .resp-btn-full { width: 100% !important; justify-content: center !important; }

            /* Formularios — padding reducido */
            .resp-form { padding: 16px !important; }

            /* Ocultar en móvil */
            .resp-hide-mobile { display: none !important; }

            /* Mostrar solo en móvil */
            .resp-show-mobile { display: flex !important; }
          }
          @media (min-width: 640px) {
            .resp-show-mobile { display: none !important; }
          }
        `}
      </style>

      <Sidebar
        isOpen={isSidebarOpen}
        toggle={() => setIsSidebarOpen(!isSidebarOpen)}
        auth={auth}
        onLogout={handleLogout}
        openProveedorModal={() => setIsProvModalOpen(true)}
        adminUnreadMessages={adminUnreadMessages}
        hasAccess={hasAccess}
      />

      <ModalProveedorRegistro isOpen={isProvModalOpen} onClose={() => setIsProvModalOpen(false)} />

      {/* Modal de documentos legales pendientes — bloquea el acceso hasta que firmen */}
      {!legalDone && (auth.isAuth || trabAuth) && (
        <LegalDocsModal
          auth={auth.isAuth ? auth : null}
          trabAuth={trabAuth || null}
          onDone={() => setLegalDone(true)}
        />
      )}

      {/* Asistente Julieta — siempre visible para el personal */}
      <Suspense fallback={null}><Julieta auth={auth} businessName={globalSettings.businessName || 'Mi Empresa'} adminUnreadMessages={adminUnreadMessages} /></Suspense>

      {auth.isAuth && ['ADMIN', 'SUPER_ADMIN'].includes((auth.role || '').toUpperCase()) && <Suspense fallback={null}><Aviso2FA /></Suspense>}

      {/* Julieta Pública — visible para visitantes y clientes (no propietarios) */}
      {/* En la pantalla de pago del hotel el botón flotante taparía «Continuar» */}
      {(!auth.isAuth || (auth.role || '').toUpperCase() === 'CLIENT') && !/^\/(reservar-hotel|hotel)\//.test(window.location.pathname) && <Suspense fallback={null}><JulietaPublica /></Suspense>}

      {/* El portal del proveedor trae su propio menú */}
      <nav style={{ ...mainNav, padding: isMobile ? '12px 16px' : '15px 80px', ...(window.location.pathname === '/proveedor/portal' ? { display: 'none' } : {}) }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '10px' : '20px' }}>
          {auth.isAuth && (hasAccess('props') || hasAccess('exps') || ROLES_POR_MODULO.includes((auth.role || '').toUpperCase())) && (
            <button onClick={() => setIsSidebarOpen(true)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: '2px', flexShrink: 0 }}><Menu size={24} /></button>
          )}
          <Link to="/" style={{ textDecoration: 'none', color: 'white' }}>
            <div style={{ ...logo, fontSize: isMobile ? '1.1rem' : '1.5rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: isMobile ? '140px' : 'none' }}>
              {globalSettings.businessName || 'Mi Empresa'}
            </div>
          </Link>
        </div>
        <div style={{ display: 'flex', gap: isMobile ? '6px' : '12px', alignItems: 'center' }}>
          {!isMobile && <Link to="/" style={navL}>Inicio</Link>}
          {hayHoteles && <Link to="/hoteles" style={{ ...navL, fontSize: isMobile ? 13 : 14 }}>Hoteles</Link>}
          {auth.isAuth ? (
            <>
              {(['CLIENT', 'OWNER'].includes((auth.role || '').toUpperCase())) && (
                <>
                  {/* Propietario: solo Estado de Cuenta y Análisis */}
                  {(auth.role || '').toUpperCase() === 'OWNER' ? (
                    <>
                      {!isMobile && (
                        <Link to="/propietario/estado-cuenta" style={{ ...navL, display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <Wallet size={15} color="white" /> Estado de Cuenta
                        </Link>
                      )}
                      {!isMobile && (
                        <Link to="/propietario/analisis" style={{ ...navL, display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <BarChart3 size={15} color="white" /> Análisis
                        </Link>
                      )}
                      {!isMobile && (
                        <Link to="/propietario/calendario" style={{ ...navL, display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <Calendar size={15} color="white" /> Calendario
                        </Link>
                      )}
                      {/* 🔔 Campana de notificaciones propietario */}
                      <div style={{ position: 'relative' }}>
                        <Link
                          to="/propietario/notificaciones"
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '50%', position: 'relative', flexShrink: 0 }}
                        >
                          <Bell size={16} color="white" />
                          {ownerUnreadNotifs > 0 && (
                            <span style={{ position: 'absolute', top: -4, right: -4, background: '#ef4444', color: 'white', borderRadius: '50%', width: 18, height: 18, fontSize: 10, fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #003366' }}>
                              {ownerUnreadNotifs > 9 ? '9+' : ownerUnreadNotifs}
                            </span>
                          )}
                        </Link>
                      </div>
                    </>
                  ) : (
                    /* Cliente: Mis Reservas + Mensajes */
                    <>
                      {!isMobile && (
                        <Link to="/mis-reservas" style={{ ...navL, display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <Calendar size={15} color="white" /> Mis Reservas
                        </Link>
                      )}
                      {!isMobile && (
                        <Link to="/mensajes" style={{ ...navL, display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <MessageCircle size={15} color="white" /> Mensajes
                        </Link>
                      )}
                    </>
                  )}
                </>
              )}
              {/* 🔔 Campana de notificaciones (solo admin) */}
              {isAdmin && (
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => { setShowNotifDrop(v => !v); if (!showNotifDrop) markNotifsRead(); }}
                    style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', position: 'relative', flexShrink: 0 }}
                  >
                    <Bell size={16} color="white" />
                    {unreadNotifCount > 0 && (
                      <span style={{ position: 'absolute', top: -4, right: -4, background: '#ef4444', color: 'white', borderRadius: '50%', width: 18, height: 18, fontSize: 10, fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #003366' }}>
                        {unreadNotifCount > 9 ? '9+' : unreadNotifCount}
                      </span>
                    )}
                  </button>

                  {showNotifDrop && (
                    <>
                      <div onClick={() => setShowNotifDrop(false)} style={{ position: 'fixed', inset: 0, zIndex: 8999 }} />
                      <div style={{ position: 'absolute', top: 44, right: 0, width: 320, background: 'white', borderRadius: 16, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', zIndex: 9000, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                        <div style={{ padding: '14px 18px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: 900, fontSize: 14, color: '#1e293b' }}>🔔 Notificaciones</span>
                          <button onClick={() => setShowNotifDrop(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 18 }}>✕</button>
                        </div>
                        <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                          {notifications.length === 0 ? (
                            <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Sin notificaciones recientes</div>
                          ) : notifications.map(n => (
                            <a key={n.id} href={n.url} onClick={() => setShowNotifDrop(false)}
                              style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 18px', borderBottom: '1px solid #f8fafc', textDecoration: 'none', background: 'white', transition: 'background .15s' }}
                              onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                              onMouseLeave={e => e.currentTarget.style.background = 'white'}
                            >
                              <span style={{ fontSize: 20, flexShrink: 0, marginTop: 1 }}>{n.icon}</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', lineHeight: 1.3, marginBottom: 2 }}>{n.title}</div>
                                {n.subtitle && <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>{n.subtitle}</div>}
                                <div style={{ fontSize: 11, color: '#94a3b8' }}>{fmtNotifTime(n.timestamp)}</div>
                              </div>
                            </a>
                          ))}
                        </div>
                        {'serviceWorker' in navigator && 'PushManager' in window && (
                          <div style={{ padding: '12px 18px', borderTop: '1px solid #f1f5f9', background: '#f8fafc' }}>
                            <button
                              onClick={togglePush}
                              disabled={pushLoading}
                              style={{ width: '100%', padding: '9px 14px', borderRadius: 10, border: 'none', cursor: pushLoading ? 'wait' : 'pointer', fontWeight: 700, fontSize: 12, background: pushEnabled ? '#fef2f2' : '#003366', color: pushEnabled ? '#dc2626' : 'white', transition: 'all .2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                            >
                              <Bell size={13} />
                              {pushLoading ? 'Procesando…' : pushEnabled ? 'Desactivar notificaciones push' : 'Activar notificaciones push'}
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              {(auth.role || '').toUpperCase() === 'VENDEDOR' && !isMobile && (
                <Link to="/vendedor/portal" style={{ ...navL, display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <Briefcase size={15} color="white" /> Mi Portal
                </Link>
              )}
              <Link
                to={
                  (auth.role || '').toUpperCase() === 'OWNER'    ? '/propietario/perfil' :
                  (auth.role || '').toUpperCase() === 'VENDEDOR' ? '/vendedor/portal'    :
                  '/perfil'
                }
                style={{ ...navL, display: 'flex', alignItems: 'center', gap: '6px', maxWidth: isMobile ? 100 : 'none', overflow: 'hidden' }}
              >
                <User size={16} color="white" />
                {!isMobile && (auth.displayName || auth.username)}
              </Link>
              <button
                onClick={handleLogout}
                style={{ display:'flex', alignItems:'center', gap:'4px', padding: isMobile ? '7px 10px' : '8px 16px', background:'rgba(255,255,255,0.15)', color:'white', border:'1px solid rgba(255,255,255,0.3)', borderRadius:'20px', fontWeight:'700', fontSize: isMobile ? '11px' : '12px', cursor:'pointer', whiteSpace: 'nowrap' }}
              >
                {isMobile ? <LogOut size={14} /> : 'Cerrar sesión'}
              </button>
            </>
          ) : trabAuth ? (
            <>
              {!isMobile && (
                <Link to="/trabajador/tareas" style={{ ...navL, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <User size={16} color="white" /> {trabAuth.nombreCompleto}
                </Link>
              )}
              <button
                onClick={() => { localStorage.removeItem('trabAuth'); window.dispatchEvent(new Event('trabAuthChanged')); window.location.href = '/trabajador/login'; }}
                style={{ display:'flex', alignItems:'center', gap:'4px', padding: isMobile ? '7px 10px' : '8px 16px', background:'rgba(255,255,255,0.15)', color:'white', border:'1px solid rgba(255,255,255,0.3)', borderRadius:'20px', fontWeight:'700', fontSize:'12px', cursor:'pointer' }}
              >
                {isMobile ? <LogOut size={14} /> : 'Cerrar sesión'}
              </button>
            </>
          ) : (
            <Link to="/login" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: isMobile ? '8px 14px' : '9px 20px', background: 'white', color: '#003366', borderRadius: '20px', fontWeight: '800', fontSize: '13px', textDecoration: 'none', border: 'none', whiteSpace: 'nowrap' }}>
              <User size={15} color="#003366" /> {isMobile ? 'Entrar' : 'Iniciar sesión'}
            </Link>
          )}
        </div>
      </nav>

      <Suspense fallback={cargandoPantalla}>
      <Routes>
        <Route path="/" element={<HomeView properties={properties} experiences={experiences} auth={auth} settings={globalSettings} />} />
        <Route path="/login" element={<Login onLogin={setAuth} auth={auth} onLogout={handleLogout} />} />
        <Route path="/verificar-email" element={<VerificarEmail />} />
        <Route path="/registro" element={<Registro setAuth={setAuth} />} />
        <Route path="/registro-propietario" element={<RegistroPropietario onLogin={setAuth} />} /> 
        <Route path="/pago-confirmado" element={<PagoConfirmado userAuth={auth} />} />
        <Route path="/pagar-reserva"   element={<PagarReserva  userAuth={auth} />} />
        <Route path="/guia/:slug" element={<GuiaHuesped />} />
        <Route path="/propiedad/:slug"  element={<PropiedadPublica auth={auth} settings={globalSettings} />} />
        <Route path="/experiencia/:slug" element={<ExperienciaPublica auth={auth} settings={globalSettings} />} />
        <Route path="/hotel/:slug" element={<V2><HotelPublico /></V2>} />
        <Route path="/hoteles" element={<V2><HotelesPagina /></V2>} />
        <Route path="/checkin/hotel/:token" element={<V2><HotelCheckIn /></V2>} />
        <Route path="/pago-hotel/:token" element={<V2><HotelPago /></V2>} />
        <Route path="/mi-cotizacion/:token" element={<V2><CotizacionCliente /></V2>} />
        <Route path="/admin/fidelizacion" element={hasAccess('admin', 'fidelizacion') ? <V2><Fidelizacion /></V2> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/cotizaciones" element={(['ADMIN', 'SUPER_ADMIN', 'VENDEDOR_INTERNO'].includes((auth.role || '').toUpperCase()) || (ROLES_POR_MODULO.includes((auth.role || '').toUpperCase()) ? hasAccess('admin', 'cotizaciones') : (() => { try { return !!JSON.parse(auth.permissions || 'null')?.cotizaciones; } catch { return false; } })())) ? <V2><Cotizaciones /></V2> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/opinion-hotel/:token" element={<V2><HotelOpinion /></V2>} />
        <Route path="/reservar-hotel/:slug" element={<V2><HotelReservar auth={auth} acceso={<Login onLogin={setAuth} enSitio />} /></V2>} />
        <Route path="/propietarios" element={<PortalPropietarios />} />
        <Route path="/checkin/:token"          element={<CheckInDigital />} />
        <Route path="/checkin/apto/:aptoToken" element={<CheckInDigital />} />
        <Route path="/confirmar-oferta/:token" element={<ConfirmarOferta />} />
        {/* ── Portal de Colaboradoras ── */}
        <Route path="/camarera/login"     element={<LoginCamarera />} />
        <Route path="/camarera/registro"  element={<RegistroCamarera />} />
        <Route path="/camarera/pendiente" element={<PendingScreen onLogout={() => { localStorage.removeItem('camareraAuth'); window.location.href = '/camarera/login'; }}/>} />
        <Route path="/camarera/portal"    element={<PortalCamarera />} />
        <Route path="/personal"           element={<PortalPersonal />} />
        {/* ── Portal de Trabajadores (Google OAuth) ── */}
        <Route path="/trabajador/login"     element={<LoginTrabajador />} />
        <Route path="/trabajador/registro"  element={<RegistroTrabajador />} />
        <Route path="/trabajador/pendiente" element={<PendienteTrabajador />} />
        <Route path="/trabajador/portal"    element={<PortalTrabajador />} />
        <Route path="/trabajador/tareas"    element={<TareasHoy />} />
        <Route path="/perfil" element={auth.isAuth ? <ClientePerfil userAuth={auth} /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/verificar-identidad" element={auth.isAuth ? <VerificacionIdentidad /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/mensajes" element={auth.isAuth ? <PaginaMensajes auth={auth} mode="client" /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/mis-reservas" element={auth.isAuth ? <><MisReservas auth={auth} /><V2><MisReservasHotel /></V2></> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/propietario/perfil" element={auth.isAuth && (auth.role || '').toUpperCase() === 'OWNER' ? <PropietarioPerfil auth={auth} /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/propietario/estado-cuenta" element={auth.isAuth && (auth.role || '').toUpperCase() === 'OWNER' ? <PropietarioEstadoCuenta auth={auth} /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/propietario/analisis" element={auth.isAuth && (auth.role || '').toUpperCase() === 'OWNER' ? <AnalisisPropietario auth={auth} /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/propietario/notificaciones" element={auth.isAuth && (auth.role || '').toUpperCase() === 'OWNER' ? <PropietarioNotificaciones auth={auth} /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/propietario/calendario" element={auth.isAuth && (auth.role || '').toUpperCase() === 'OWNER' ? <PropietarioCalendario auth={auth} /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/reservar" element={auth.isAuth ? <CheckoutReserva userAuth={auth} /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/reservar-legacy" element={auth.isAuth ? <ReservaAlojamiento mode="cliente" userAuth={auth} /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        
        <Route path="/admin/dashboard" element={hasAccess('dashboard') ? <DashboardGeneral /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/calendario" element={hasAccess('props', 'calendario') ? <MultiPropertyCalendar /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/reservas" element={hasAccess('props', 'reservas') ? <ReservationsManager /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/nueva-reserva" element={hasAccess('props', 'reservas') ? <ReservaAlojamiento /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        
        <Route path="/admin/AgregarPropiedades" element={hasAccess('props', 'propiedades') ? <AgregarPropiedades refreshList={fetchData} /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        
        <Route path="/admin/propiedades-lista" element={hasAccess('props', 'propiedades') ? <ListaPropiedades /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/listas-chequeo" element={hasAccess('props', 'propiedades') ? <V2><ChecklistsAptos /></V2> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/experiencia" element={hasAccess('exps', 'experiencias') ? <AdminExperiencias refreshList={fetchData} /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/reservas-experiencias" element={hasAccess('exps', 'reservas_exp') ? <ExperienceReservationsManager /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/experiencias-lista" element={hasAccess('exps', 'experiencias') ? <ListaExperiencias /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        
        <Route path="/admin/gestion-proveedores" element={hasAccess('admin', 'proveedores') ? <AdminProveedores /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/proveedores-lista"        element={<Navigate to="/admin/gestion-proveedores" replace />} />
        <Route path="/admin/pago-proveedor"           element={<Navigate to="/admin/gestion-proveedores" replace />} />
        <Route path="/admin/lista-pagos-proveedores"  element={<Navigate to="/admin/gestion-proveedores" replace />} />
        <Route path="/admin/pagos-experiencias"       element={<Navigate to="/admin/gestion-proveedores" replace />} />
        
        <Route path="/admin/registrar-propietario" element={hasAccess('admin', 'propietarios') ? <AgregarPropietario /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/propietarios" element={hasAccess('admin', 'propietarios') ? <ListaPropietarios /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/estados-cuenta" element={hasAccess('admin', 'estados_cuenta') ? <EstadoCuentaPropietarios /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/egresos" element={hasAccess('admin', 'contabilidad') ? <EgresosDashboard /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/guias-huesped" element={hasAccess('admin', 'propiedades') ? <GestionGuias />       : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/check-ins"    element={hasAccess('admin', 'reservas') ? <GestionCheckIns /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/manual"       element={hasAccess('admin', '*') ? <ManualUso />          : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/revenue"      element={hasAccess('admin', 'analytics') ? <RevenueManager />           : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/analytics-hub" element={hasAccess('admin', 'analytics') ? <AnalyticsHub />              : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/analytics"    element={<Navigate to="/admin/analytics-hub?tab=analytics" replace />} />
        <Route path="/admin/kpis"         element={<Navigate to="/admin/analytics-hub?tab=kpis"      replace />} />
        <Route path="/admin/mantenimiento-preventivo" element={hasAccess('admin', 'mantenimiento') ? <MantenimientoPreventivo /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/ocr-recibos" element={hasAccess('admin', 'contabilidad') ? <OcrRecibos />              : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/facturacion" element={hasAccess('admin', 'contabilidad') ? <Facturacion />        : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/recibos-caja" element={hasAccess('admin', 'contabilidad') ? <ReciboCajaList /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/solicitudes-pendientes" element={hasAccess('admin', 'reservas') ? <SolicitudesPendientes userAuth={auth} /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/otros-ingresos" element={hasAccess('admin', 'otros_ingresos') ? <OtrosIngresos /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/otros-gastos"   element={hasAccess('admin', 'otros_gastos') ? <OtrosGastos />   : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/distribuciones" element={hasAccess('admin', 'contabilidad') ? <DistribucionUtilidades /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/gestion-humana" element={hasAccess('admin', 'nomina') ? <GestionHumana /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/trabajadores-lista" element={hasAccess('admin', 'nomina') ? <ListaTrabajadores /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/pago-nomina" element={hasAccess('admin', 'nomina') ? <PagoNomina /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/lista-pagos-nomina" element={hasAccess('admin', 'pagos_nomina') ? <ListaPagoNomina /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/contabilidad" element={hasAccess('admin', 'contabilidad') ? <ContabilidadDashboard /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/mensajes" element={hasAccess('admin', 'mensajes') ? <AdminMessages /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/roles"          element={hasAccess('admin', 'roles') ? <AdminRoles />          : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/configuracion" element={hasAccess('admin', 'configuracion') ? <ConfiguracionSistema /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/superadmin" element={(auth.role || '').toUpperCase() === 'SUPER_ADMIN' ? <SuperAdminPanel /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/crm" element={hasAccess('admin', 'crm') ? <CRM /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/leads" element={hasAccess('admin', 'crm') ? <LeadPipeline /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/automatizaciones" element={hasAccess('admin', 'automatizaciones') ? <AutomationEngine /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/vyara" element={<Navigate to="/admin/analytics-hub?tab=resumen" replace />} />
        <Route path="/admin/documentos-legales" element={hasAccess('admin', 'documentos') ? <AdminDocumentos /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/estudio-mercado" element={hasAccess('admin', 'mercado') ? <EstudioMercado /> : <SinAcceso auth={auth} onLogin={setAuth} />} />

        {/* RUTAS DE MANTENIMIENTO INTEGRADAS */}
        <Route path="/admin/camareras" element={hasAccess('admin', 'camareras') ? <PanelCamareras /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/mantenimiento" element={hasAccess('admin', 'mantenimiento') ? <MaintenanceDashboard /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/mantenimiento/nuevo" element={hasAccess('admin', 'mantenimiento') ? <RegisterMaintenance /> : <SinAcceso auth={auth} onLogin={setAuth} />} />

        {/* VYARA 2.0 — HOTELES Y MODELOS DE NEGOCIO */}
        <Route path="/admin/hoteles" element={hasAccess('admin', 'hoteles') ? <V2><HotelesAdmin /></V2> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/conciliacion-experiencias" element={hasAccess('admin', 'conciliacion_exp') ? <V2><ConciliacionExperiencias /></V2> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/guias" element={hasAccess('admin', 'guias') ? <V2><GuiasAdmin /></V2> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/popups" element={hasAccess('admin', 'popups') ? <V2><PopupsAdmin /></V2> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/hoteles/general" element={veHotelesRuta ? <V2><HotelesTablero /></V2> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        {RUTAS_ANTERIORES.map(r => <Route key={r} path={`/admin/hoteles/${r}`} element={<V2><IrAlHotel ruta={r} /></V2>} />)}
        <Route path="/admin/hotel/:hid" element={veHotelesRuta ? <V2><HotelEspacio modulos={modulosHotel} /></V2> : <SinAcceso auth={auth} onLogin={setAuth} />}>
          <Route index element={<V2><ResumenHotel modulos={modulosHotel} /></V2>} />
          {modulosHotel.filter(m => PANTALLAS_HOTEL[m.ruta]).map(m => <Route key={m.ruta} path={m.ruta} element={<V2>{PANTALLAS_HOTEL[m.ruta]}</V2>} />)}
        </Route>
        <Route path="/admin/hoteles/:id" element={hasAccess('admin', 'hoteles') ? <V2><HotelesAdmin /></V2> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/contabilidad-negocio" element={hasAccess('admin', 'contabilidad_alcance') ? <V2><ContabilidadAlcance /></V2> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/nomina-negocio" element={hasAccess('admin', 'nomina_alcance') ? <V2><NominaAlcance /></V2> : <SinAcceso auth={auth} onLogin={setAuth} />} />

        {/* JULIETA IA */}
        <Route path="/admin/julieta" element={hasAccess('admin', 'julieta') ? <AdminJulieta /> : <SinAcceso auth={auth} onLogin={setAuth} />} />

        {/* VENDEDORES ALIADOS */}
        <Route path="/registro-aliado" element={<RegistroVendedor onBack={() => window.location.replace('/login')} />} />
        <Route path="/vendedor/portal" element={auth.isAuth && (auth.role || '').toUpperCase() === 'VENDEDOR' ? <PortalVendedor auth={auth} onLogout={() => { localStorage.removeItem('auth'); window.location.href = '/login'; }} /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        <Route path="/admin/vendedores" element={hasAccess('admin', 'vendedores') ? <AdminVendedores /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
        {/* Páginas públicas del portal de aliados */}
        <Route path="/cotizacion/:shortCode" element={<CotizacionPage />} />

        <Route path="/registro-proveedor" element={<RegistroProveedor onBack={() => window.location.replace('/login')} />} />
        <Route path="/registro-proveedor/:tipo" element={<RegistroProveedor onBack={() => window.location.replace('/login')} />} />
        <Route path="/proveedor/completar-registro" element={<ProveedorCompletarRegistro auth={auth} setAuth={setAuth} />} />
        <Route path="/proveedor/portal" element={auth.isAuth && (auth.role || '').toUpperCase() === 'PROVEEDOR' ? <PortalProveedor auth={auth} onLogout={handleLogout} /> : <SinAcceso auth={auth} onLogin={setAuth} />} />
      </Routes>
      </Suspense>
    </div>
  );
}

// --- ESTILOS ---
const sidebarStyle = { position: 'fixed', top: 0, left: 0, height: '100%', width: '280px', backgroundColor: 'white', boxShadow: '4px 0 20px rgba(0,0,0,0.15)', padding: '30px 20px', display: 'flex', flexDirection: 'column' };
const overlayStyle = { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)' };
const sidebarLinksContainer = { display: 'flex', flexDirection: 'column', gap: '5px', flex: 1 };
const sidebarLink = { textDecoration: 'none', color: '#333', fontSize: '13px', fontWeight: '600', padding: '10px 15px', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '10px', transition: '0.2s' };
const sidebarSectionTitle = { fontSize: '11px', fontWeight: '900', color: '#475569', marginTop: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' };
const sidebarLogoutBtn = { width: '100%', padding: '12px', background: '#f8fafc', border: 'none', borderRadius: '10px', color: '#FF385C', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' };
const menuBadge = { minWidth: '22px', height: '22px', borderRadius: '999px', background: '#e11d48', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '900', padding: '0 6px' };
const mainNav = { backgroundColor: '#003366', color: 'white', padding: '15px 80px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 1000 };
const logo = { fontSize: '1.5rem', fontWeight: '900' };
const navL = { textDecoration: 'none', color: 'white', fontSize: '14px', fontWeight: '700' };
const loginCard = { borderRadius: '32px', backgroundColor: 'white', width: '400px', boxShadow: '0 20px 40px rgba(0,0,0,0.08)', overflow: 'hidden' };
const sliderInput = { padding: '14px', borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '14px', width: '100%', boxSizing: 'border-box' };
const actionBtnFull = { padding: '16px', background: '#003366', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '800', cursor: 'pointer', width: '100%' };
